

import React, { useState, useEffect, useRef } from 'react';
import {
  SafeAreaView,
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  StatusBar,
  ActivityIndicator,
  Alert,
  Modal,
  TextInput,
  Dimensions,
  Animated,
  RefreshControl,
  Switch,
  Platform,
  FlatList,
  Image,
  Linking,
  Share,
  BackHandler,
  KeyboardAvoidingView,
} from 'react-native';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import * as Clipboard from 'expo-clipboard';
import AsyncStorage from '@react-native-async-storage/async-storage';

const { width, height } = Dimensions.get('window');

const BRAND = {
  primary: '#0e4e96',
  secondary: '#fdb913',
  accent: '#19abb5',
  background: '#F8FAFC',
  surface: '#FFFFFF',
  text: '#0F172A',
  textSecondary: '#64748B',
  border: '#E2E8F0',
  success: '#10B981',
  warning: '#F59E0B',
  danger: '#EF4444',
  info: '#0EA5E9',
  purple: '#8B5CF6',
  aiGradient: ['#6366F1', '#8B5CF6'],
};

// Enhanced mock data with comprehensive business features
const generateModuleData = (module) => {
  const baseData = {
    id: module.id,
    name: module.name,
    icon: module.icon,
    color: module.color,
    desc: module.desc,
    category: module.category,
    load: module.load,
    
    // Dashboard Stats - Comprehensive Business Metrics
    dashboard: {
      totalRevenue: `$${(Math.random() * 500 + 100).toFixed(2)}K`,
      monthlyRevenue: `$${(Math.random() * 50 + 20).toFixed(2)}K`,
      yoyGrowth: `${(Math.random() * 30 + 5).toFixed(1)}%`,
      profitMargin: `${(Math.random() * 30 + 15).toFixed(1)}%`,
      activeProjects: Math.floor(Math.random() * 50) + 20,
      pendingTasks: Math.floor(Math.random() * 100) + 30,
      completionRate: `${Math.floor(Math.random() * 30) + 70}%`,
      customerSatisfaction: `${Math.floor(Math.random() * 20) + 80}%`,
      teamProductivity: `${Math.floor(Math.random() * 40) + 60}%`,
      budgetUtilization: `${Math.floor(Math.random() * 50) + 50}%`,
      growthRate: `${Math.floor(Math.random() * 40) + 10}%`,
      employeeEngagement: `${Math.floor(Math.random() * 30) + 70}%`,
      operationalEfficiency: `${Math.floor(Math.random() * 35) + 65}%`,
      customerRetention: `${Math.floor(Math.random() * 25) + 75}%`,
      marketShare: `${(Math.random() * 20 + 10).toFixed(1)}%`,
      roi: `${(Math.random() * 50 + 100).toFixed(1)}%`,
      cashFlow: `$${(Math.random() * 100 + 50).toFixed(2)}K`,
    },
    
    // Business Items/Records with Categories
    items: Array.from({ length: 30 }, (_, i) => ({
      id: `item_${i}`,
      title: `${module.name} Record ${i + 1}`,
      description: `Detailed description for ${String(module.name ?? '').toLowerCase()} record ${i + 1} with business context and requirements`,
      status: ['pending', 'in-progress', 'completed', 'on-hold', 'cancelled'][i % 5],
      priority: ['low', 'medium', 'high', 'critical'][i % 4],
      category: ['sales', 'marketing', 'operations', 'finance', 'hr', 'it'][i % 6],
      department: ['Sales', 'Marketing', 'Operations', 'Finance', 'HR', 'IT'][i % 6],
      assignedTo: `User ${(i % 8) + 1}`,
      assignedAvatar: ['AJ', 'SW', 'MC', 'ED', 'RK', 'PS', 'JL', 'TM'][i % 8],
      assignedEmail: `user${(i % 8) + 1}@company.com`,
      dueDate: new Date(Date.now() + (i * 86400000)).toISOString().split('T')[0],
      startDate: new Date(Date.now() - (i * 172800000)).toISOString().split('T')[0],
      createdAt: new Date(Date.now() - (i * 172800000)).toISOString(),
      updatedAt: new Date(Date.now() - (i * 86400000)).toISOString(),
      value: `$${(Math.random() * 10000 + 1000).toFixed(2)}`,
      budget: `$${(Math.random() * 5000 + 500).toFixed(2)}`,
      cost: `$${(Math.random() * 3000 + 300).toFixed(2)}`,
      tags: ['urgent', 'feature', 'bug', 'enhancement', 'documentation', 'client', 'internal'].slice(0, i % 7 + 1),
      comments: Math.floor(Math.random() * 10),
      attachments: Math.floor(Math.random() * 5),
      dependencies: Math.floor(Math.random() * 3),
      riskLevel: ['low', 'medium', 'high'][i % 3],
      phase: ['planning', 'execution', 'review', 'deployment'][i % 4],
      milestone: `M${Math.floor(i / 5) + 1}`,
      kpis: [`KPI-${i + 1}`, `KPI-${i + 2}`],
      client: `Client ${String.fromCharCode(65 + (i % 5))}`,
      clientContact: `contact${i % 5 + 1}@client.com`,
      contractValue: `$${(Math.random() * 50000 + 10000).toFixed(2)}`,
      timeline: `${Math.floor(Math.random() * 30) + 15} days`,
    })),
    
    // Comprehensive Team Structure
    team: Array.from({ length: 12 }, (_, i) => ({
      id: `member_${i}`,
      name: ['Alex Johnson', 'Sarah Williams', 'Michael Chen', 'Emily Davis', 'Robert Kim', 'Priya Sharma', 'James Lee', 'Taylor Morgan', 'David Wilson', 'Lisa Anderson', 'Kevin Brown', 'Maria Garcia'][i],
      role: ['Project Manager', 'Lead Developer', 'Senior Analyst', 'QA Engineer', 'DevOps', 'Data Scientist', 'UI/UX Designer', 'Business Analyst', 'Sales Executive', 'Marketing Specialist', 'HR Manager', 'Finance Controller'][i],
      avatar: ['AJ', 'SW', 'MC', 'ED', 'RK', 'PS', 'JL', 'TM', 'DW', 'LA', 'KB', 'MG'][i],
      department: ['Management', 'Engineering', 'Analytics', 'Quality', 'Operations', 'Data', 'Design', 'Business', 'Sales', 'Marketing', 'HR', 'Finance'][i],
      email: `team${i}@company.com`,
      phone: `+1 (555) 123-${1000 + i}`,
      extension: `${100 + i}`,
      tasks: [12, 18, 8, 15, 6, 10, 14, 9, 11, 7, 5, 13][i],
      completedTasks: [10, 15, 6, 12, 5, 8, 11, 7, 9, 6, 4, 10][i],
      performance: `${Math.floor(Math.random() * 30) + 70}%`,
      engagement: `${Math.floor(Math.random() * 40) + 60}%`,
      status: ['online', 'away', 'offline', 'busy'][i % 4],
      location: ['Office', 'Remote', 'Hybrid'][i % 3],
      joinedDate: new Date(Date.now() - (i * 365 * 86400000)).toISOString().split('T')[0],
      skills: ['JavaScript', 'React', 'Node.js', 'Python', 'SQL', 'AWS', 'Docker', 'Kubernetes', 'Salesforce', 'Marketing', 'HRIS', 'Accounting'].slice(0, i % 6 + 1),
      certifications: ['PMP', 'AWS', 'Google Cloud', 'Scrum', 'Salesforce'][i % 5] || 'None',
      manager: i > 3 ? ['Alex Johnson', 'Sarah Williams'][i % 2] : 'None',
      salaryGrade: `G${Math.floor(i / 3) + 6}`,
      billableRate: `$${(Math.random() * 50 + 100).toFixed(0)}/hr`,
      utilization: `${Math.floor(Math.random() * 40) + 60}%`,
    })),
    
    // Comprehensive Documents with Business Categories
    documents: Array.from({ length: 20 }, (_, i) => ({
      id: `doc_${i}`,
      name: [
        'Project Requirements.pdf',
        'Quarterly Financial Report.xlsx', 
        'Design Specifications.docx',
        'API Documentation.md',
        'User Manual.zip',
        'Test Cases.csv',
        'Annual Budget Plan.pdf',
        'Board Meeting Minutes.docx',
        'Code Review Guidelines.md',
        'Deployment Guide.pdf',
        'Security Audit Report.docx',
        'Performance Metrics Report.xlsx',
        'Contract Agreement.pdf',
        'Marketing Strategy.docx',
        'HR Policies Manual.pdf',
        'Sales Forecast.xlsx',
        'Risk Assessment.docx',
        'Compliance Checklist.pdf',
        'Vendor Agreement.pdf',
        'Training Materials.zip'
      ][i],
      size: ['2.4 MB', '1.8 MB', '850 KB', '320 KB', '15.2 MB', '450 KB', '3.1 MB', '720 KB', '210 KB', '4.5 MB', '1.2 MB', '2.9 MB', '1.5 MB', '980 KB', '3.8 MB', '2.1 MB', '1.1 MB', '860 KB', '2.7 MB', '12.4 MB'][i],
      type: ['pdf', 'excel', 'word', 'markdown', 'archive', 'csv', 'pdf', 'word', 'markdown', 'pdf', 'word', 'excel', 'pdf', 'word', 'pdf', 'excel', 'word', 'pdf', 'pdf', 'archive'][i],
      category: ['Technical', 'Financial', 'Technical', 'Technical', 'Training', 'QA', 'Financial', 'Administrative', 'Technical', 'Technical', 'Security', 'Analytics', 'Legal', 'Marketing', 'HR', 'Sales', 'Risk', 'Compliance', 'Legal', 'Training'][i],
      uploaded: new Date(Date.now() - (i * 5 * 86400000)).toISOString().split('T')[0],
      uploadedBy: ['Alex Johnson', 'Sarah Williams', 'Michael Chen', 'Emily Davis', 'Robert Kim', 'Priya Sharma'][i % 6],
      uploadedById: `user_${i % 6}`,
      version: `v${(i % 3) + 1}.${i % 10}`,
      description: `${module.name} related document - important information for business operations`,
      accessLevel: ['Public', 'Team', 'Management', 'Confidential'][i % 4],
      retentionPeriod: `${(i % 5) + 1} years`,
      lastAccessed: new Date(Date.now() - (i * 2 * 86400000)).toISOString().split('T')[0],
      downloadCount: Math.floor(Math.random() * 100),
      reviewDate: new Date(Date.now() + (i * 30 * 86400000)).toISOString().split('T')[0],
      keywords: ['requirements', 'financial', 'design', 'api', 'user', 'testing', 'budget', 'meeting', 'code', 'deployment', 'security', 'performance', 'contract', 'marketing', 'hr', 'sales', 'risk', 'compliance', 'vendor', 'training'][i],
    })),
    
    // Advanced Business Analytics
    analytics: {
      monthlyTrend: Array.from({ length: 12 }, (_, i) => ({
        month: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][i],
        revenue: Math.floor(Math.random() * 100) + 50,
        profit: Math.floor(Math.random() * 80) + 30,
        expenses: Math.floor(Math.random() * 40) + 20,
        customers: Math.floor(Math.random() * 200) + 100,
      })),
      quarterlyPerformance: [
        { quarter: 'Q1', revenue: Math.floor(Math.random() * 300) + 200, profit: Math.floor(Math.random() * 200) + 100, target: 250 },
        { quarter: 'Q2', revenue: Math.floor(Math.random() * 350) + 250, profit: Math.floor(Math.random() * 220) + 120, target: 280 },
        { quarter: 'Q3', revenue: Math.floor(Math.random() * 400) + 300, profit: Math.floor(Math.random() * 250) + 150, target: 320 },
        { quarter: 'Q4', revenue: Math.floor(Math.random() * 450) + 350, profit: Math.floor(Math.random() * 280) + 180, target: 350 },
      ],
      categoryDistribution: [
        { category: 'Development', value: 35, color: '#6366F1', budget: 150000, actual: 142000 },
        { category: 'Testing', value: 25, color: '#10B981', budget: 80000, actual: 76000 },
        { category: 'Documentation', value: 15, color: '#F59E0B', budget: 50000, actual: 45000 },
        { category: 'Meetings', value: 15, color: '#8B5CF6', budget: 40000, actual: 38000 },
        { category: 'Research', value: 10, color: '#EC4899', budget: 30000, actual: 28000 },
      ],
      performanceMetrics: {
        efficiency: Math.floor(Math.random() * 30) + 70,
        accuracy: Math.floor(Math.random() * 25) + 75,
        timeliness: Math.floor(Math.random() * 35) + 65,
        quality: Math.floor(Math.random() * 20) + 80,
        collaboration: Math.floor(Math.random() * 40) + 60,
        innovation: Math.floor(Math.random() * 50) + 50,
        scalability: Math.floor(Math.random() * 40) + 60,
        reliability: Math.floor(Math.random() * 30) + 70,
        compliance: Math.floor(Math.random() * 35) + 65,
        sustainability: Math.floor(Math.random() * 45) + 55,
      },
      kpis: [
        { name: 'Revenue Growth', current: 18, target: 15, unit: '%', trend: 'up' },
        { name: 'Customer Retention', current: 92, target: 90, unit: '%', trend: 'up' },
        { name: 'Project Delivery', current: 88, target: 85, unit: '%', trend: 'up' },
        { name: 'Team Satisfaction', current: 86, target: 85, unit: '%', trend: 'up' },
        { name: 'Operational Costs', current: 12, target: 15, unit: '%', trend: 'down' },
        { name: 'Market Share', current: 22, target: 20, unit: '%', trend: 'up' },
        { name: 'Employee Turnover', current: 8, target: 10, unit: '%', trend: 'down' },
        { name: 'Customer Satisfaction', current: 94, target: 90, unit: '%', trend: 'up' },
      ],
      financials: {
        totalAssets: `$${(Math.random() * 5000 + 1000).toFixed(2)}K`,
        totalLiabilities: `$${(Math.random() * 2000 + 500).toFixed(2)}K`,
        equity: `$${(Math.random() * 3000 + 500).toFixed(2)}K`,
        cashOnHand: `$${(Math.random() * 500 + 100).toFixed(2)}K`,
        debtRatio: `${(Math.random() * 30 + 20).toFixed(1)}%`,
        currentRatio: `${(Math.random() * 2 + 1.5).toFixed(2)}`,
        quickRatio: `${(Math.random() * 1.5 + 1).toFixed(2)}`,
        workingCapital: `$${(Math.random() * 800 + 200).toFixed(2)}K`,
      },
    },
    
    // Business Activities with Context
    recentActivities: Array.from({ length: 20 }, (_, i) => ({
      id: `activity_${i}`,
      type: ['task_completed', 'document_uploaded', 'comment_added', 'status_changed', 'assignment_updated', 'meeting_scheduled', 'bug_fixed', 'feature_added', 'payment_received', 'invoice_sent', 'contract_signed', 'proposal_submitted', 'milestone_achieved', 'risk_identified', 'issue_resolved', 'training_completed', 'audit_completed', 'compliance_met', 'vendor_selected', 'client_meeting'][i % 20],
      description: [
        'Completed project milestone ahead of schedule',
        'Uploaded new financial documentation for Q3 review',
        'Added comments to the quarterly budget proposal',
        'Changed project status from planning to execution',
        'Updated team assignment for the new client project',
        'Scheduled board meeting for strategic planning',
        'Fixed critical security vulnerability in production',
        'Added new payment gateway integration feature',
        'Received payment for invoice #INV-2023-0456',
        'Sent invoice to client for consulting services',
        'Signed new contract with major vendor',
        'Submitted proposal for government project',
        'Achieved Q3 revenue milestone',
        'Identified potential supply chain risk',
        'Resolved critical customer support issue',
        'Completed compliance training for all staff',
        'Completed annual financial audit successfully',
        'Met all regulatory compliance requirements',
        'Selected new vendor for cloud services',
        'Conducted quarterly review with key client',
      ][i % 20],
      user: ['Alex Johnson', 'Sarah Williams', 'Michael Chen', 'Emily Davis', 'Robert Kim', 'Priya Sharma', 'James Lee', 'Taylor Morgan'][i % 8],
      userAvatar: ['AJ', 'SW', 'MC', 'ED', 'RK', 'PS', 'JL', 'TM'][i % 8],
      userRole: ['Project Manager', 'Lead Developer', 'Senior Analyst', 'QA Engineer', 'DevOps', 'Data Scientist', 'UI/UX Designer', 'Business Analyst'][i % 8],
      timestamp: new Date(Date.now() - (i * 3600000)).toISOString(),
      module: module.name,
      priority: ['low', 'medium', 'high', 'critical'][i % 4],
      impact: ['low', 'medium', 'high'][i % 3],
      category: ['Project', 'Financial', 'HR', 'Operations', 'Sales', 'Marketing'][i % 6],
      clientInvolved: i % 3 === 0 ? `Client ${String.fromCharCode(65 + (i % 5))}` : null,
      revenueImpact: i % 4 === 0 ? `$${(Math.random() * 10000 + 1000).toFixed(2)}` : null,
      department: ['Engineering', 'Finance', 'HR', 'Sales', 'Marketing', 'Operations'][i % 6],
    })),
    
    // Business Calendar with Meetings
    calendar: Array.from({ length: 15 }, (_, i) => ({
      id: `event_${i}`,
      title: [
        'Board Meeting',
        'Client Presentation',
        'Sprint Planning',
        'Code Review',
        'Product Demo',
        'Retrospective',
        'Training Session',
        'Budget Review',
        'Quarterly Review',
        'Team Building',
        'Vendor Meeting',
        'Investor Call',
        'Compliance Audit',
        'Marketing Strategy',
        'Sales Forecast Review'
      ][i],
      date: new Date(Date.now() + (i * 86400000)).toISOString().split('T')[0],
      time: ['9:00 AM', '11:00 AM', '2:00 PM', '3:30 PM', '10:00 AM', '4:00 PM', '1:00 PM', '10:30 AM', '3:00 PM', '2:30 PM', '11:30 AM', '9:30 AM', '1:30 PM', '4:30 PM', '10:00 AM'][i],
      duration: ['1h', '2h', '1.5h', '1h', '2h', '1.5h', '3h', '2h', '3h', '4h', '1h', '1.5h', '2h', '2.5h', '1h'][i],
      participants: 3 + (i % 8),
      location: ['Conference Room A', 'Zoom', 'Conference Room B', 'Slack', 'Microsoft Teams', 'Office', 'Training Room', 'Board Room', 'Client Office', 'Hotel Conference', 'Virtual', 'HQ', 'Branch Office', 'Off-site', 'Virtual'][i],
      type: ['meeting', 'review', 'planning', 'training', 'demo', 'social', 'client', 'vendor', 'board', 'team'][i % 10],
      organizer: ['Alex Johnson', 'Sarah Williams', 'Michael Chen', 'Emily Davis'][i % 4],
      agenda: `Discussion about ${['Q3 results', 'new features', 'budget allocation', 'team performance', 'client feedback', 'market trends', 'product roadmap', 'hiring plan'][i % 8]}`,
      priority: ['low', 'medium', 'high'][i % 3],
      recurring: i % 5 === 0,
      attachments: Math.floor(Math.random() * 3),
      notes: i % 3 === 0 ? 'Important meeting, please prepare' : null,
      clientInvolved: i % 4 === 0 ? `Client ${String.fromCharCode(65 + (i % 5))}` : null,
      budget: i % 6 === 0 ? `$${(Math.random() * 5000 + 1000).toFixed(2)}` : null,
    })),
    
    // Business Settings with Advanced Options
    settings: {
      notifications: true,
      emailAlerts: true,
      pushNotifications: true,
      smsAlerts: false,
      autoSync: true,
      syncFrequency: '30min',
      darkMode: false,
      theme: 'light',
      language: 'en',
      timezone: 'UTC',
      dateFormat: 'MM/DD/YYYY',
      currency: 'USD',
      decimalPlaces: 2,
      exportFormat: 'csv',
      dataRetention: '90 days',
      accessLevel: 'full',
      twoFactorAuth: true,
      sessionTimeout: '30min',
      dataBackup: true,
      backupFrequency: 'daily',
      auditLog: true,
      complianceMode: false,
      developerMode: false,
      apiAccess: true,
      webhookEnabled: false,
      autoArchive: true,
      archiveAfter: '60 days',
      dataEncryption: true,
      privacyMode: false,
      performanceMode: true,
    },
    
    // AI Insights for Business Decisions
    aiInsights: [
      "Team productivity has increased by 15% this quarter, suggesting the new workflow is effective.",
      "Consider automating repetitive tasks to improve efficiency and reduce operational costs by ~20%.",
      "Customer satisfaction scores show positive trend with a 12% increase in NPS.",
      "Budget utilization is optimal at 78%, with room for strategic investments.",
      "Recommend additional training for new features to accelerate adoption.",
      "Sales conversion rate could improve by 8% with better lead qualification.",
      "Market analysis suggests expanding to the APAC region could increase revenue by 25%.",
      "Operational costs are 5% below industry average, indicating good cost control.",
      "Customer churn risk identified in segment B - recommend proactive engagement.",
      "Supply chain optimization could reduce delivery times by 15%.",
    ],
    
    // Business Integrations
    integrations: [
      { name: 'Slack', connected: true, icon: 'slack', category: 'Communication', usage: 'High', lastUsed: 'Today' },
      { name: 'GitHub', connected: true, icon: 'github', category: 'Development', usage: 'High', lastUsed: 'Yesterday' },
      { name: 'Jira', connected: false, icon: 'jira', category: 'Project Management', usage: 'Medium', lastUsed: '2 days ago' },
      { name: 'Google Drive', connected: true, icon: 'google-drive', category: 'Storage', usage: 'High', lastUsed: 'Today' },
      { name: 'Zoom', connected: true, icon: 'video', category: 'Communication', usage: 'Medium', lastUsed: 'Today' },
      { name: 'Salesforce', connected: false, icon: 'salesforce', category: 'CRM', usage: 'Low', lastUsed: '1 week ago' },
      { name: 'QuickBooks', connected: true, icon: 'calculator', category: 'Finance', usage: 'High', lastUsed: 'Today' },
      { name: 'HubSpot', connected: true, icon: 'hubspot', category: 'Marketing', usage: 'Medium', lastUsed: 'Yesterday' },
      { name: 'Asana', connected: true, icon: 'clipboard-check', category: 'Project Management', usage: 'Medium', lastUsed: 'Today' },
      { name: 'Stripe', connected: true, icon: 'credit-card', category: 'Payments', usage: 'High', lastUsed: 'Today' },
      { name: 'AWS', connected: true, icon: 'aws', category: 'Cloud', usage: 'High', lastUsed: 'Today' },
      { name: 'Microsoft Teams', connected: false, icon: 'microsoft', category: 'Communication', usage: 'Low', lastUsed: '2 weeks ago' },
    ],
    
    // Business Clients
    clients: Array.from({ length: 10 }, (_, i) => ({
      id: `client_${i}`,
      name: `Client ${String.fromCharCode(65 + i)} Corporation`,
      contact: `John Doe ${i + 1}`,
      email: `contact${i + 1}@clientcorp.com`,
      phone: `+1 (555) 100-${1000 + i}`,
      status: ['active', 'active', 'active', 'inactive', 'prospective'][i % 5],
      contractValue: `$${(Math.random() * 100000 + 50000).toFixed(2)}`,
      startDate: new Date(Date.now() - (i * 90 * 86400000)).toISOString().split('T')[0],
      renewalDate: new Date(Date.now() + (i * 180 * 86400000)).toISOString().split('T')[0],
      satisfaction: `${Math.floor(Math.random() * 30) + 70}%`,
      projects: Math.floor(Math.random() * 5) + 1,
      industry: ['Technology', 'Finance', 'Healthcare', 'Retail', 'Manufacturing'][i % 5],
      location: ['New York', 'San Francisco', 'Chicago', 'London', 'Singapore'][i % 5],
      accountManager: ['Alex Johnson', 'Sarah Williams'][i % 2],
    })),
    
    // Business Reports
    reports: Array.from({ length: 8 }, (_, i) => ({
      id: `report_${i}`,
      name: ['Monthly Financial Report', 'Quarterly Performance Review', 'Annual Audit Report', 'Project Status Report', 'Team Productivity Analysis', 'Customer Satisfaction Survey', 'Market Analysis Report', 'Risk Assessment Report'][i],
      type: ['financial', 'performance', 'audit', 'project', 'productivity', 'survey', 'analysis', 'risk'][i],
      frequency: ['monthly', 'quarterly', 'annual', 'weekly', 'monthly', 'quarterly', 'annual', 'quarterly'][i],
      lastGenerated: new Date(Date.now() - (i * 7 * 86400000)).toISOString().split('T')[0],
      nextDue: new Date(Date.now() + (i * 7 * 86400000)).toISOString().split('T')[0],
      status: ['generated', 'pending', 'generated', 'in-progress', 'generated', 'pending', 'generated', 'in-progress'][i],
      generatedBy: ['Alex Johnson', 'Sarah Williams', 'Michael Chen', 'Emily Davis'][i % 4],
      size: ['2.5 MB', '3.1 MB', '4.8 MB', '1.2 MB', '2.1 MB', '890 KB', '3.5 MB', '2.8 MB'][i],
      format: ['pdf', 'excel', 'pdf', 'word', 'pdf', 'pdf', 'pdf', 'pdf'][i],
      recipients: Math.floor(Math.random() * 10) + 5,
      confidential: i % 3 === 0,
    })),
  };
  
  return baseData;
};

const ModuleDetailScreen = ({ navigation, route }) => {
  const { module } = route.params || {};
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [moduleData, setModuleData] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [showNewModal, setShowNewModal] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showExport, setShowExport] = useState(false);
  const [showCalendar, setShowCalendar] = useState(false);
  const [showAI, setShowAI] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [showClients, setShowClients] = useState(false);
  const [showReports, setShowReports] = useState(false);
  const [newItem, setNewItem] = useState({ 
    title: '', 
    description: '', 
    priority: 'medium',
    type: 'task',
    category: 'operations',
    department: 'General',
    dueDate: new Date(Date.now() + 7 * 86400000).toISOString().split('T')[0],
    budget: '1000',
    client: 'New Client',
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [settings, setSettings] = useState({
    notifications: true,
    emailAlerts: true,
    pushNotifications: true,
    autoSync: true,
    darkMode: false,
    exportFormat: 'csv',
    twoFactorAuth: true,
    dataBackup: true,
    auditLog: true,
  });
  const [aiInput, setAiInput] = useState('');
  const [aiMessages, setAiMessages] = useState([
    { role: 'assistant', content: `Hello! I'm your ${module?.name || 'ERP'} AI assistant. I can help you analyze business data, generate insights, and optimize operations. How can I assist you today?` }
  ]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [showItemDetail, setShowItemDetail] = useState(false);
  
  const fadeAnim = useRef(new Animated.Value(0)).current;
  const slideAnim = useRef(new Animated.Value(20)).current;
  const aiScaleAnim = useRef(new Animated.Value(0)).current;
  const modalScaleAnim = useRef(new Animated.Value(0.8)).current;

  // Enhanced tabs with business features
  const TABS = [
    { id: 'overview', label: 'Overview', icon: 'view-dashboard', color: module?.color },
    { id: 'items', label: 'Items', icon: 'format-list-bulleted', color: module?.color },
    { id: 'team', label: 'Team', icon: 'account-group', color: module?.color },
    { id: 'analytics', label: 'Analytics', icon: 'chart-bar', color: module?.color },
    { id: 'docs', label: 'Documents', icon: 'file-document', color: module?.color },
    { id: 'calendar', label: 'Calendar', icon: 'calendar', color: module?.color },
    { id: 'integrations', label: 'Integrations', icon: 'link', color: module?.color },
    { id: 'clients', label: 'Clients', icon: 'account-tie', color: module?.color },
    { id: 'reports', label: 'Reports', icon: 'file-chart', color: module?.color },
  ];

  // Enhanced handleBackPress function
  const handleBackPress = () => {
    // Priority order for closing modals
    if (showItemDetail) {
      setShowItemDetail(false);
      return;
    }
    
    if (showAI) {
      setShowAI(false);
      return;
    }
    
    if (showNewModal) {
      setShowNewModal(false);
      return;
    }
    
    if (showSettings) {
      setShowSettings(false);
      return;
    }
    
    if (showExport) {
      setShowExport(false);
      return;
    }
    
    if (showCalendar) {
      setShowCalendar(false);
      return;
    }
    
    if (showClients) {
      setShowClients(false);
      return;
    }
    
    if (showReports) {
      setShowReports(false);
      return;
    }
    
    // If no modals are open, go back to previous screen
    navigation.goBack();
  };

  // Enhanced Android back button handling
  useEffect(() => {
    const backAction = () => {
      handleBackPress();
      return true; // Prevent default back behavior
    };

    const backHandler = BackHandler.addEventListener('hardwareBackPress', backAction);

    return () => backHandler.remove();
  }, [navigation, showNewModal, showSettings, showExport, showAI, showCalendar, showClients, showReports, showItemDetail]);

  useEffect(() => {
    if (module) {
      loadModuleData();
      startAnimations();
      
      // Set navigation options with enhanced back button
      navigation.setOptions({
        headerLeft: () => (
          <TouchableOpacity 
            onPress={handleBackPress}
            style={{ 
              marginLeft: 15, 
              padding: 8,
              flexDirection: 'row',
              alignItems: 'center'
            }}
            hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
          >
            <MaterialCommunityIcons name="arrow-left" size={24} color="white" />
            <Text style={{ 
              color: 'white', 
              marginLeft: 4, 
              fontSize: 14, 
              fontWeight: '600' 
            }}>
              Back
            </Text>
          </TouchableOpacity>
        ),
        headerRight: () => (
          <View style={{ flexDirection: 'row', marginRight: 15 }}>
            <TouchableOpacity 
              onPress={() => setShowAI(true)} 
              style={{ marginRight: 12, padding: 6 }}
            >
              <MaterialCommunityIcons name="robot" size={22} color="white" />
            </TouchableOpacity>
            <TouchableOpacity 
              onPress={() => setShowNewModal(true)} 
              style={{ marginRight: 12, padding: 6 }}
            >
              <MaterialCommunityIcons name="plus" size={22} color="white" />
            </TouchableOpacity>
            <TouchableOpacity 
              onPress={() => setShowSettings(true)} 
              style={{ padding: 6 }}
            >
              <MaterialCommunityIcons name="cog" size={22} color="white" />
            </TouchableOpacity>
          </View>
        ),
        title: module.name,
        headerStyle: { 
          backgroundColor: module.color,
          elevation: 0,
          shadowOpacity: 0,
          height: 60,
        },
        headerTintColor: 'white',
        headerTitleStyle: {
          fontWeight: '700',
          fontSize: 18,
        },
        headerBackTitleVisible: false,
      });
    }
  }, [module, navigation, showNewModal, showSettings, showExport, showAI, showCalendar, showClients, showReports, showItemDetail]);

  const startAnimations = () => {
    Animated.parallel([
      Animated.timing(fadeAnim, {
        toValue: 1,
        duration: 600,
        useNativeDriver: true,
      }),
      Animated.spring(slideAnim, {
        toValue: 0,
        friction: 8,
        tension: 40,
        useNativeDriver: true,
      }),
    ]).start();

    // Animate AI button
    setTimeout(() => {
      Animated.spring(aiScaleAnim, {
        toValue: 1,
        friction: 6,
        useNativeDriver: true,
      }).start();
    }, 800);
  };

  const loadModuleData = async (isRefresh = false) => {
    if (!isRefresh) setLoading(true);
    
    try {
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 800));
      
      // Generate mock data
      const data = generateModuleData(module);
      setModuleData(data);
      
    } catch (error) {
      console.error('Error loading module data:', error);
      Alert.alert('Error', 'Failed to load module data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const onRefresh = () => {
    setRefreshing(true);
    loadModuleData(true);
  };

  // Enhanced Business CRUD Operations
  const handleCreateItem = () => {
    if (!newItem.title.trim()) {
      Alert.alert('Validation Error', 'Please enter a title for the item');
      return;
    }

    const newItemData = {
      id: `${module.id}_item_${Date.now()}`,
      title: newItem.title,
      description: newItem.description || 'New business item created',
      status: 'pending',
      priority: newItem.priority,
      type: newItem.type,
      category: newItem.category,
      department: newItem.department,
      assignedTo: 'You',
      assignedAvatar: 'ME',
      assignedEmail: 'you@company.com',
      dueDate: newItem.dueDate,
      startDate: new Date().toISOString().split('T')[0],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      value: `$${(Math.random() * 10000 + 1000).toFixed(2)}`,
      budget: `$${parseFloat(newItem.budget || 1000).toFixed(2)}`,
      cost: `$${(Math.random() * 500 + 300).toFixed(2)}`,
      tags: ['new', newItem.category],
      comments: 0,
      attachments: 0,
      dependencies: 0,
      riskLevel: 'medium',
      phase: 'planning',
      milestone: 'M1',
      kpis: ['KPI-1', 'KPI-2'],
      client: newItem.client,
      clientContact: 'contact@client.com',
      contractValue: `$${(Math.random() * 50000 + 10000).toFixed(2)}`,
      timeline: '30 days',
    };

    // Update local state
    setModuleData(prev => ({
      ...prev,
      items: [newItemData, ...prev.items],
      dashboard: {
        ...prev.dashboard,
        pendingTasks: prev.dashboard.pendingTasks + 1,
        activeProjects: prev.dashboard.activeProjects + 1,
      },
      recentActivities: [
        {
          id: `activity_${Date.now()}`,
          type: 'task_created',
          description: `Created new item: "${newItem.title}"`,
          user: 'You',
          userAvatar: 'ME',
          userRole: 'User',
          timestamp: new Date().toISOString(),
          module: module.name,
          priority: newItem.priority,
          impact: 'medium',
          category: 'Project',
          department: newItem.department,
        },
        ...prev.recentActivities
      ]
    }));

    Alert.alert('Success', 'Business item created successfully!');
    setShowNewModal(false);
    setNewItem({ 
      title: '', 
      description: '', 
      priority: 'medium', 
      type: 'task',
      category: 'operations',
      department: 'General',
      dueDate: new Date(Date.now() + 7 * 86400000).toISOString().split('T')[0],
      budget: '1000',
      client: 'New Client',
    });
  };

  const handleUpdateItemStatus = (itemId, newStatus) => {
    const item = moduleData.items.find(item => item.id === itemId);
    setModuleData(prev => ({
      ...prev,
      items: prev.items.map(item =>
        item.id === itemId ? { ...item, status: newStatus, updatedAt: new Date().toISOString() } : item
      ),
      recentActivities: [
        {
          id: `activity_${Date.now()}`,
          type: 'status_changed',
          description: `Changed status of "${item.title}" to ${newStatus}`,
          user: 'You',
          userAvatar: 'ME',
          userRole: 'User',
          timestamp: new Date().toISOString(),
          module: module.name,
          priority: item.priority,
          impact: 'medium',
          category: item.category,
          department: item.department,
        },
        ...prev.recentActivities
      ]
    }));
    
    Alert.alert('Status Updated', `Item "${item.title}" marked as ${newStatus.replace('-', ' ')}`);
  };

  const handleDeleteItem = (itemId) => {
    const item = moduleData.items.find(item => item.id === itemId);
    Alert.alert(
      'Delete Business Item',
      `Are you sure you want to delete "${item.title}"? This action cannot be undone.`,
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Delete',
          style: 'destructive',
          onPress: () => {
            setModuleData(prev => ({
              ...prev,
              items: prev.items.filter(item => item.id !== itemId),
              dashboard: {
                ...prev.dashboard,
                pendingTasks: prev.dashboard.pendingTasks - 1,
              },
              recentActivities: [
                {
                  id: `activity_${Date.now()}`,
                  type: 'item_deleted',
                  description: `Deleted item: "${item.title}"`,
                  user: 'You',
                  userAvatar: 'ME',
                  userRole: 'User',
                  timestamp: new Date().toISOString(),
                  module: module.name,
                  priority: item.priority,
                  impact: 'high',
                  category: item.category,
                  department: item.department,
                },
                ...prev.recentActivities
              ]
            }));
            Alert.alert('Success', 'Item deleted successfully');
          },
        },
      ]
    );
  };

  const handleExportData = async (format) => {
    try {
      const data = moduleData?.items?.slice(0, 20) || [];
      const exportData = data.map(item => ({
        Title: item.title,
        Description: item.description,
        Status: item.status,
        Priority: item.priority,
        Category: item.category,
        Department: item.department,
        'Assigned To': item.assignedTo,
        'Due Date': item.dueDate,
        Value: item.value,
        Budget: item.budget,
        Client: item.client,
      }));

      if (format === 'csv') {
        const csvContent = convertToCSV(exportData);
        await Clipboard.setStringAsync(csvContent);
        Alert.alert('Copied to Clipboard', 'CSV data copied to clipboard. You can paste it into Excel or Google Sheets.');
      } else if (format === 'json') {
        const jsonString = JSON.stringify(exportData, null, 2);
        await Clipboard.setStringAsync(jsonString);
        Alert.alert('Copied to Clipboard', 'JSON data copied to clipboard');
      } else if (format === 'excel') {
        // Simulate Excel export
        const excelContent = exportData.map(item => Object.values(item).join('\t')).join('\n');
        await Clipboard.setStringAsync(excelContent);
        Alert.alert('Copied to Clipboard', 'Excel format data copied. Paste into Excel.');
      }
      
      setShowExport(false);
    } catch (error) {
      Alert.alert('Export Failed', 'Could not export data. Please try again.');
    }
  };

  const convertToCSV = (data) => {
    if (data.length === 0) return '';
    const headers = Object.keys(data[0]).join(',');
    const rows = data.map(item => Object.values(item).map(val => 
      typeof val === 'string' && val.includes(',') ? `"${val}"` : val
    ).join(','));
    return [headers, ...rows].join('\n');
  };

  const handleShare = async () => {
    try {
      const message = `${module.name} Module Summary - ${new Date().toLocaleDateString()}\n\n` +
        `Key Business Metrics:\n` +
        `• Total Revenue: ${moduleData?.dashboard?.totalRevenue}\n` +
        `• Active Projects: ${moduleData?.dashboard?.activeProjects}\n` +
        `• Completion Rate: ${moduleData?.dashboard?.completionRate}\n` +
        `• Profit Margin: ${moduleData?.dashboard?.profitMargin}\n` +
        `• Customer Satisfaction: ${moduleData?.dashboard?.customerSatisfaction}\n\n` +
        `View detailed analytics in the app for more insights.`;
      
      await Share.share({
        message,
        title: `${module.name} Business Module Summary`,
      });
    } catch (error) {
      console.error('Share error:', error);
      Alert.alert('Share Failed', 'Could not share module data');
    }
  };

  const handleAIMessage = () => {
    if (!aiInput.trim()) return;

    const userMessage = { role: 'user', content: aiInput };
    setAiMessages(prev => [...prev, userMessage]);
    setAiInput('');

    // Simulate AI response with business context
    setTimeout(() => {
      const responses = [
        `Based on the ${module.name} business data, I recommend optimizing resource allocation. Current utilization is at ${moduleData?.dashboard?.budgetUtilization}.`,
        `The analytics show a ${moduleData?.dashboard?.yoyGrowth} YoY growth. Consider investing in marketing to accelerate this trend.`,
        `Customer satisfaction is at ${moduleData?.dashboard?.customerSatisfaction}. Focus on improving support response times.`,
        `Team productivity is ${moduleData?.dashboard?.teamProductivity}. Recommend team training for the new ${module.name} features.`,
        `Financial analysis shows ${moduleData?.dashboard?.roi} ROI. This indicates good investment efficiency.`,
        `Market share is ${moduleData?.analytics?.kpis?.[5]?.current || '22'}%. Consider competitive analysis for growth strategies.`,
        `Operational efficiency at ${moduleData?.dashboard?.operationalEfficiency}. Automation could improve this by 15-20%.`,
        `Cash flow is ${moduleData?.dashboard?.cashFlow}. Ensure adequate liquidity for upcoming projects.`,
      ];
      
      const randomResponse = responses[Math.floor(Math.random() * responses.length)];
      const aiMessage = { role: 'assistant', content: randomResponse };
      
      setAiMessages(prev => [...prev, aiMessage]);
    }, 1200);
  };

  const handleViewItemDetails = (item) => {
    setSelectedItem(item);
    setShowItemDetail(true);
  };

  const filteredItems = moduleData?.items?.filter(item => {
    if (searchQuery && !String(item.title ?? '').toLowerCase().includes(searchQuery.toLowerCase()) && 
        !String(item.description ?? '').toLowerCase().includes(searchQuery.toLowerCase()) &&
        !String(item.client ?? '').toLowerCase().includes(searchQuery.toLowerCase())) {
      return false;
    }
    
    if (filterStatus !== 'all' && item.status !== filterStatus) {
      return false;
    }
    
    if (filterPriority !== 'all' && item.priority !== filterPriority) {
      return false;
    }
    
    if (filterCategory !== 'all' && item.category !== filterCategory) {
      return false;
    }
    
    return true;
  }) || [];

  // Enhanced Business Header
  const renderHeader = () => (
    <LinearGradient 
      colors={[module.color, `${module.color}DD`, `${module.color}BB`]} 
      style={styles.header}
    >
      <View style={styles.headerContent}>
        <View style={[styles.moduleIcon, { backgroundColor: 'rgba(255,255,255,0.2)' }]}>
          <MaterialCommunityIcons name={module.icon} size={32} color="white" />
        </View>
        <View style={styles.headerText}>
          <View style={{ flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap' }}>
            <Text style={styles.headerTitle}>{module.name}</Text>
            <View style={styles.proBadge}>
              <MaterialCommunityIcons name="crown" size={10} color={BRAND.text} />
              <Text style={styles.proBadgeText}>ENTERPRISE</Text>
            </View>
          </View>
          <Text style={styles.headerSubtitle}>{module.desc}</Text>
          <View style={styles.headerMeta}>
            <View style={styles.metaItem}>
              <MaterialCommunityIcons name="folder" size={10} color="rgba(255,255,255,0.8)" />
              <Text style={styles.metaText}>{module.category}</Text>
            </View>
            <View style={styles.metaItem}>
              <MaterialCommunityIcons name="gauge" size={10} color="rgba(255,255,255,0.8)" />
              <Text style={styles.metaText}>Load: {module.load}%</Text>
            </View>
            <View style={styles.metaItem}>
              <MaterialCommunityIcons name="calendar" size={10} color="rgba(255,255,255,0.8)" />
              <Text style={styles.metaText}>{new Date().toLocaleDateString()}</Text>
            </View>
          </View>
        </View>
      </View>
      
      {/* Enhanced Quick Stats Bar */}
      <View style={styles.quickStats}>
        <View style={styles.quickStat}>
          <View style={styles.quickStatIcon}>
            <MaterialCommunityIcons name="cash" size={14} color="white" />
          </View>
          <Text style={styles.quickStatText}>{moduleData?.dashboard?.totalRevenue || '$0K'}</Text>
          <Text style={styles.quickStatLabel}>Revenue</Text>
        </View>
        <View style={styles.quickStat}>
          <View style={styles.quickStatIcon}>
            <MaterialCommunityIcons name="clipboard-check" size={14} color="white" />
          </View>
          <Text style={styles.quickStatText}>{moduleData?.dashboard?.activeProjects || 0}</Text>
          <Text style={styles.quickStatLabel}>Projects</Text>
        </View>
        <View style={styles.quickStat}>
          <View style={styles.quickStatIcon}>
            <MaterialCommunityIcons name="chart-line" size={14} color="white" />
          </View>
          <Text style={styles.quickStatText}>{moduleData?.dashboard?.completionRate || '0%'}</Text>
          <Text style={styles.quickStatLabel}>Complete</Text>
        </View>
        <View style={styles.quickStat}>
          <View style={styles.quickStatIcon}>
            <MaterialCommunityIcons name="account-group" size={14} color="white" />
          </View>
          <Text style={styles.quickStatText}>{moduleData?.team?.length || 0}</Text>
          <Text style={styles.quickStatLabel}>Team</Text>
        </View>
      </View>
    </LinearGradient>
  );

  // Enhanced Tabs with scroll and more features
  const renderTabs = () => (
    <View style={styles.tabsContainer}>
      <ScrollView 
        horizontal 
        showsHorizontalScrollIndicator={false} 
        contentContainerStyle={styles.tabsContent}
      >
        {TABS.map(tab => (
          <TouchableOpacity
            key={tab.id}
            style={[styles.tab, activeTab === tab.id && styles.activeTab]}
            onPress={() => setActiveTab(tab.id)}
            activeOpacity={0.7}
          >
            <MaterialCommunityIcons 
              name={tab.icon} 
              size={16} 
              color={activeTab === tab.id ? 'white' : module.color} 
            />
            <Text style={[styles.tabText, activeTab === tab.id && styles.activeTabText]}>
              {tab.label}
            </Text>
            {tab.id === 'items' && moduleData?.items?.length > 0 && (
              <View style={[styles.tabBadge, { backgroundColor: activeTab === tab.id ? 'rgba(255,255,255,0.3)' : module.color + '20' }]}>
                <Text style={[styles.tabBadgeText, { color: activeTab === tab.id ? 'white' : module.color }]}>
                  {moduleData.items.length}
                </Text>
              </View>
            )}
          </TouchableOpacity>
        ))}
      </ScrollView>
    </View>
  );

  // Enhanced Overview with Business Insights
  const renderOverview = () => (
    <ScrollView 
      refreshControl={
        <RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor={module.color} />
      }
      style={styles.scrollContent}
      showsVerticalScrollIndicator={false}
    >
      {/* AI Business Insights Card */}
      <LinearGradient colors={BRAND.aiGradient} style={styles.aiInsightCard}>
        <View style={styles.aiInsightHeader}>
          <MaterialCommunityIcons name="robot" size={20} color="white" />
          <Text style={styles.aiInsightTitle}>AI Business Insight</Text>
          <TouchableOpacity onPress={() => setShowAI(true)} style={styles.aiInsightButton}>
            <MaterialCommunityIcons name="chat" size={18} color="white" />
          </TouchableOpacity>
        </View>
        <Text style={styles.aiInsightText}>
          {moduleData?.aiInsights?.[Math.floor(Math.random() * moduleData?.aiInsights?.length)] || 
           'AI analysis shows optimal business performance with opportunities for growth.'}
        </Text>
        <View style={styles.aiInsightFooter}>
          <Text style={styles.aiInsightTimestamp}>Updated just now</Text>
          <TouchableOpacity onPress={() => setShowAI(true)}>
            <Text style={styles.aiInsightAction}>Ask AI</Text>
          </TouchableOpacity>
        </View>
      </LinearGradient>

      {/* Key Business Metrics Grid */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Key Business Metrics</Text>
        <View style={styles.statsGrid}>
          {Object.entries(moduleData?.dashboard || {})
            .filter(([key]) => 
              ['totalRevenue', 'profitMargin', 'yoyGrowth', 'customerSatisfaction', 'roi', 'marketShare'].includes(key)
            )
            .map(([key, value], index) => (
            <TouchableOpacity 
              key={key}
              style={styles.statCard}
              onPress={() => Alert.alert(
                key.replace(/([A-Z])/g, ' $1').toUpperCase(),
                `Current: ${value}\n\nThis metric shows the ${key.toLowerCase()} performance for this module.`,
                [{ text: 'OK', style: 'default' }]
              )}
            >
              <Text style={styles.statValue}>{value}</Text>
              <Text style={styles.statLabel}>
                {key.replace(/([A-Z])/g, ' $1')}
              </Text>
              <View style={styles.statTrend}>
                <MaterialCommunityIcons 
                  name={index % 3 === 0 ? "trending-up" : index % 3 === 1 ? "trending-down" : "trending-neutral"} 
                  size={12} 
                  color={index % 3 === 0 ? BRAND.success : index % 3 === 1 ? BRAND.danger : BRAND.warning} 
                />
                <Text style={[
                  styles.statTrendText,
                  { color: index % 3 === 0 ? BRAND.success : index % 3 === 1 ? BRAND.danger : BRAND.warning }
                ]}>
                  <Text>{index % 3 === 0 ? '+12%' : index % 3 === 1 ? '-5%' : '0%'}</Text>
                </Text>
              </View>
            </TouchableOpacity>
          ))}
        </View>
      </View>

      {/* Business Quick Actions */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Business Actions</Text>
        <View style={styles.actionsGrid}>
          <TouchableOpacity 
            style={styles.actionCard} 
            onPress={() => setShowNewModal(true)}
          >
            <LinearGradient colors={[module.color, `${module.color}DD`]} style={styles.actionIcon}>
              <MaterialCommunityIcons name="plus" size={22} color="white" />
            </LinearGradient>
            <Text style={styles.actionText}>New Item</Text>
          </TouchableOpacity>
          
          <TouchableOpacity 
            style={styles.actionCard}
            onPress={() => setShowExport(true)}
          >
            <LinearGradient colors={[BRAND.success, `${BRAND.success}DD`]} style={styles.actionIcon}>
              <MaterialCommunityIcons name="export" size={22} color="white" />
            </LinearGradient>
            <Text style={styles.actionText}>Export</Text>
          </TouchableOpacity>
          
          <TouchableOpacity 
            style={styles.actionCard}
            onPress={handleShare}
          >
            <LinearGradient colors={[BRAND.info, `${BRAND.info}DD`]} style={styles.actionIcon}>
              <MaterialCommunityIcons name="share-variant" size={22} color="white" />
            </LinearGradient>
            <Text style={styles.actionText}>Share</Text>
          </TouchableOpacity>
          
          <TouchableOpacity 
            style={styles.actionCard}
            onPress={() => setShowCalendar(true)}
          >
            <LinearGradient colors={[BRAND.purple, `${BRAND.purple}DD`]} style={styles.actionIcon}>
              <MaterialCommunityIcons name="calendar" size={22} color="white" />
            </LinearGradient>
            <Text style={styles.actionText}>Calendar</Text>
          </TouchableOpacity>

          <TouchableOpacity 
            style={styles.actionCard}
            onPress={() => setShowClients(true)}
          >
            <LinearGradient colors={[BRAND.accent, `${BRAND.accent}DD`]} style={styles.actionIcon}>
              <MaterialCommunityIcons name="account-tie" size={22} color="white" />
            </LinearGradient>
            <Text style={styles.actionText}>Clients</Text>
          </TouchableOpacity>

          <TouchableOpacity 
            style={styles.actionCard}
            onPress={() => setShowReports(true)}
          >
            <LinearGradient colors={[BRAND.warning, `${BRAND.warning}DD`]} style={styles.actionIcon}>
              <MaterialCommunityIcons name="file-chart" size={22} color="white" />
            </LinearGradient>
            <Text style={styles.actionText}>Reports</Text>
          </TouchableOpacity>
        </View>
      </View>

      {/* Recent Business Activities */}
      <View style={styles.section}>
        <View style={styles.sectionHeader}>
          <Text style={styles.sectionTitle}>Recent Business Activities</Text>
          <TouchableOpacity onPress={() => Alert.alert('Activity Log', 'Showing all business activities')}>
            <Text style={styles.viewAllText}>View All</Text>
          </TouchableOpacity>
        </View>
        <View style={styles.activitiesList}>
          {moduleData?.recentActivities?.slice(0, 6).map(activity => (
            <TouchableOpacity 
              key={activity.id} 
              style={styles.activityItem}
              onPress={() => Alert.alert(
                activity.description,
                `User: ${activity.user}\nRole: ${activity.userRole}\nTime: ${new Date(activity.timestamp).toLocaleString()}\nImpact: ${activity.impact}\nCategory: ${activity.category}`
              )}
            >
              <View style={[styles.activityAvatar, { backgroundColor: `${module.color}20` }]}>
                <Text style={[styles.activityAvatarText, { color: module.color }]}>
                  {activity.userAvatar}
                </Text>
              </View>
              <View style={styles.activityContent}>
                <Text style={styles.activityText}>{activity.description}</Text>
                <View style={styles.activityMeta}>
                  <Text style={styles.activityUser}>{activity.user}</Text>
                  <Text style={styles.activityTime}>
                    {new Date(activity.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </Text>
                </View>
              </View>
              <View style={[styles.activityBadge, { backgroundColor: `${getPriorityColor(activity.priority)}20` }]}>
                <Text style={[styles.activityBadgeText, { color: getPriorityColor(activity.priority) }]}>
                  {activity.priority}
                </Text>
              </View>
            </TouchableOpacity>
          ))}
        </View>
      </View>

      {/* Upcoming Business Events */}
      <View style={styles.section}>
        <View style={styles.sectionHeader}>
          <Text style={styles.sectionTitle}>Upcoming Events</Text>
          <TouchableOpacity onPress={() => setShowCalendar(true)}>
            <Text style={styles.viewAllText}>View Calendar</Text>
          </TouchableOpacity>
        </View>
        <View style={styles.eventsPreview}>
          {moduleData?.calendar?.slice(0, 3).map(event => (
            <TouchableOpacity 
              key={event.id} 
              style={styles.eventPreviewCard}
              onPress={() => Alert.alert(
                event.title,
                `Date: ${event.date}\nTime: ${event.time}\nDuration: ${event.duration}\nLocation: ${event.location}\nOrganizer: ${event.organizer}`
              )}
            >
              <View style={[styles.eventPreviewIcon, { backgroundColor: `${module.color}15` }]}>
                <MaterialCommunityIcons 
                  name={event.type === 'meeting' ? 'account-group' : 'calendar'} 
                  size={16} 
                  color={module.color} 
                />
              </View>
              <View style={styles.eventPreviewInfo}>
                <Text style={styles.eventPreviewTitle}>{event.title}</Text>
                <Text style={styles.eventPreviewDetails}>{event.date} • {event.time}</Text>
              </View>
              <MaterialCommunityIcons name="chevron-right" size={16} color={BRAND.textSecondary} />
            </TouchableOpacity>
          ))}
        </View>
      </View>
    </ScrollView>
  );

  // Enhanced Items List with Business Features
  const renderItems = () => (
    <View style={{ flex: 1 }}>
      {/* Enhanced Search and Filter Bar */}
      <View style={styles.searchSection}>
        <View style={styles.searchBar}>
          <MaterialCommunityIcons name="magnify" size={18} color={BRAND.textSecondary} />
          <TextInput
            style={styles.searchInput}
            placeholder="Search business items..."
            value={searchQuery}
            onChangeText={setSearchQuery}
            placeholderTextColor={BRAND.textSecondary}
          />
          {searchQuery.length > 0 && (
            <TouchableOpacity onPress={() => setSearchQuery('')}>
              <MaterialCommunityIcons name="close-circle" size={16} color={BRAND.textSecondary} />
            </TouchableOpacity>
          )}
        </View>
        <TouchableOpacity 
          style={styles.filterButton}
          onPress={() => setShowFilters(!showFilters)}
        >
          <MaterialCommunityIcons name="filter-variant" size={20} color={BRAND.primary} />
          {filterStatus !== 'all' || filterPriority !== 'all' || filterCategory !== 'all' ? (
            <View style={styles.filterIndicator} />
          ) : null}
        </TouchableOpacity>
      </View>

      {/* Enhanced Filters Panel */}
      {showFilters && (
        <View style={styles.filtersPanel}>
          <View style={styles.filterHeader}>
            <Text style={styles.filterTitle}>Filters</Text>
            <TouchableOpacity 
              style={styles.clearFiltersButton}
              onPress={() => {
                setFilterStatus('all');
                setFilterPriority('all');
                setFilterCategory('all');
              }}
            >
              <Text style={styles.clearFiltersText}>Clear All</Text>
            </TouchableOpacity>
          </View>
          
          <View style={styles.filterRow}>
            <Text style={styles.filterLabel}>Status</Text>
            <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.filterChips}>
              {['all', 'pending', 'in-progress', 'completed', 'on-hold', 'cancelled'].map(status => (
                <TouchableOpacity
                  key={status}
                  style={[styles.filterChip, filterStatus === status && styles.activeFilterChip]}
                  onPress={() => setFilterStatus(status)}
                >
                  <Text style={[styles.filterChipText, filterStatus === status && styles.activeFilterChipText]}>
                    {status.charAt(0).toUpperCase() + status.slice(1)}
                  </Text>
                </TouchableOpacity>
              ))}
            </ScrollView>
          </View>
          
          <View style={styles.filterRow}>
            <Text style={styles.filterLabel}>Priority</Text>
            <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.filterChips}>
              {['all', 'low', 'medium', 'high', 'critical'].map(priority => (
                <TouchableOpacity
                  key={priority}
                  style={[
                    styles.filterChip, 
                    filterPriority === priority && styles.activeFilterChip,
                    { borderColor: getPriorityColor(priority) }
                  ]}
                  onPress={() => setFilterPriority(priority)}
                >
                  <Text style={[
                    styles.filterChipText, 
                    filterPriority === priority && styles.activeFilterChipText,
                    { color: filterPriority === priority ? 'white' : getPriorityColor(priority) }
                  ]}>
                    {priority.charAt(0).toUpperCase() + priority.slice(1)}
                  </Text>
                </TouchableOpacity>
              ))}
            </ScrollView>
          </View>
          
          <View style={styles.filterRow}>
            <Text style={styles.filterLabel}>Category</Text>
            <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.filterChips}>
              {['all', 'sales', 'marketing', 'operations', 'finance', 'hr', 'it'].map(category => (
                <TouchableOpacity
                  key={category}
                  style={[styles.filterChip, filterCategory === category && styles.activeFilterChip]}
                  onPress={() => setFilterCategory(category)}
                >
                  <Text style={[styles.filterChipText, filterCategory === category && styles.activeFilterChipText]}>
                    {category.charAt(0).toUpperCase() + category.slice(1)}
                  </Text>
                </TouchableOpacity>
              ))}
            </ScrollView>
          </View>
        </View>
      )}

      {/* Items List */}
      <FlatList
        data={filteredItems}
        keyExtractor={item => item.id}
        renderItem={({ item }) => (
          <TouchableOpacity 
            style={styles.itemCard}
            onPress={() => handleViewItemDetails(item)}
            activeOpacity={0.9}
          >
            <View style={styles.itemHeader}>
              <View style={{ flexDirection: 'row', alignItems: 'center', flex: 1 }}>
                <View style={[styles.statusBadge, { 
                  backgroundColor: getStatusColor(item.status)
                }]}>
                  <Text style={styles.statusText}>{item.status}</Text>
                </View>
                <View style={[styles.priorityBadge, { 
                  backgroundColor: getPriorityColor(item.priority)
                }]}>
                  <Text style={styles.priorityText}>{item.priority}</Text>
                </View>
                <View style={[styles.categoryBadge, { 
                  backgroundColor: `${module.color}20`,
                  borderColor: module.color
                }]}>
                  <Text style={[styles.categoryText, { color: module.color }]}>{item.category}</Text>
                </View>
              </View>
              <TouchableOpacity 
                style={styles.itemMenuButton}
                onPress={() => {
                  Alert.alert(
                    'Item Actions',
                    `Select an action for "${item.title}"`,
                    [
                      { text: 'View Details', onPress: () => handleViewItemDetails(item) },
                      { text: 'Edit', onPress: () => Alert.alert('Edit', 'Edit functionality coming soon') },
                      { text: 'Duplicate', onPress: () => Alert.alert('Duplicate', 'Duplicate functionality coming soon') },
                      { text: 'Delete', style: 'destructive', onPress: () => handleDeleteItem(item.id) },
                      { text: 'Cancel', style: 'cancel' },
                    ]
                  );
                }}
              >
                <MaterialCommunityIcons name="dots-vertical" size={18} color={BRAND.textSecondary} />
              </TouchableOpacity>
            </View>
            
            <Text style={styles.itemTitle}>{item.title}</Text>
            <Text style={styles.itemDescription}>{item.description}</Text>
            
            {/* Business Metadata */}
            <View style={styles.itemMeta}>
              <View style={styles.metaRow}>
                <View style={styles.metaItemSmall}>
                  <MaterialCommunityIcons name="account" size={12} color={BRAND.textSecondary} />
                  <Text style={styles.metaTextSmall}>{item.assignedTo}</Text>
                </View>
                <View style={styles.metaItemSmall}>
                  <MaterialCommunityIcons name="calendar" size={12} color={BRAND.textSecondary} />
                  <Text style={styles.metaTextSmall}>{item.dueDate}</Text>
                </View>
                <View style={styles.metaItemSmall}>
                  <MaterialCommunityIcons name="office-building" size={12} color={BRAND.textSecondary} />
                  <Text style={styles.metaTextSmall}>{item.department}</Text>
                </View>
              </View>
              
              {item.client && (
                <View style={styles.metaRow}>
                  <View style={styles.metaItemSmall}>
                    <MaterialCommunityIcons name="account-tie" size={12} color={BRAND.textSecondary} />
                    <Text style={styles.metaTextSmall}>{item.client}</Text>
                  </View>
                </View>
              )}
            </View>
            
            <View style={styles.itemTags}>
              {item.tags.map((tag, index) => (
                <View key={index} style={styles.tag}>
                  <Text style={styles.tagText}>{tag}</Text>
                </View>
              ))}
            </View>
            
            <View style={styles.itemFooter}>
              <View style={styles.itemFinancial}>
                <View style={styles.financialItem}>
                  <Text style={styles.financialLabel}>Value</Text>
                  <Text style={styles.financialValue}>{item.value}</Text>
                </View>
                <View style={styles.financialItem}>
                  <Text style={styles.financialLabel}>Budget</Text>
                  <Text style={styles.financialValue}>{item.budget}</Text>
                </View>
                <View style={styles.financialItem}>
                  <Text style={styles.financialLabel}>Cost</Text>
                  <Text style={[styles.financialValue, { color: BRAND.success }]}>{item.cost}</Text>
                </View>
              </View>
              
              <View style={styles.itemActions}>
                <TouchableOpacity 
                  style={[styles.itemActionButton, { backgroundColor: `${module.color}15` }]}
                  onPress={() => handleUpdateItemStatus(item.id, 'in-progress')}
                >
                  <MaterialCommunityIcons name="play" size={12} color={module.color} />
                  <Text style={[styles.itemActionText, { color: module.color }]}>Start</Text>
                </TouchableOpacity>
                
                <TouchableOpacity 
                  style={[styles.itemActionButton, { backgroundColor: `${BRAND.success}15` }]}
                  onPress={() => handleUpdateItemStatus(item.id, 'completed')}
                >
                  <MaterialCommunityIcons name="check" size={12} color={BRAND.success} />
                  <Text style={[styles.itemActionText, { color: BRAND.success }]}>Complete</Text>
                </TouchableOpacity>
              </View>
            </View>
          </TouchableOpacity>
        )}
        ListEmptyComponent={
          <View style={styles.emptyState}>
            <MaterialCommunityIcons name="package-variant" size={64} color={BRAND.border} />
            <Text style={styles.emptyStateText}>No business items found</Text>
            <Text style={styles.emptyStateSubtext}>Try adjusting your filters or create a new item</Text>
            <TouchableOpacity 
              style={[styles.emptyStateButton, { backgroundColor: module.color }]}
              onPress={() => setShowNewModal(true)}
            >
              <Text style={styles.emptyStateButtonText}>Create New Item</Text>
            </TouchableOpacity>
          </View>
        }
        contentContainerStyle={{ paddingBottom: 120 }}
        showsVerticalScrollIndicator={false}
      />
    </View>
  );

  // Enhanced Team View
  const renderTeam = () => (
    <FlatList
      data={moduleData?.team}
      keyExtractor={item => item.id}
      numColumns={2}
      renderItem={({ item }) => (
        <TouchableOpacity 
          style={styles.teamCard}
          onPress={() => Alert.alert(
            item.name,
            `Role: ${item.role}\nDepartment: ${item.department}\nEmail: ${item.email}\nPhone: ${item.phone}\nPerformance: ${item.performance}\nUtilization: ${item.utilization}\nBillable Rate: ${item.billableRate}`,
            [
              { text: 'OK', style: 'default' },
              { text: 'Contact', onPress: () => Linking.openURL(`mailto:${item.email}`) },
              { text: 'View Profile', onPress: () => Alert.alert('Profile', 'Team member profile coming soon') },
            ]
          )}
        >
          <View style={styles.teamCardHeader}>
            <View style={[styles.avatar, { backgroundColor: module.color }]}>
              <Text style={styles.avatarText}>{item.avatar}</Text>
            </View>
            <View style={[styles.statusDot, { 
              backgroundColor: 
                item.status === 'online' ? BRAND.success :
                item.status === 'away' ? BRAND.warning : 
                item.status === 'busy' ? BRAND.danger : BRAND.textSecondary
            }]} />
          </View>
          <Text style={styles.teamName}>{item.name.split(' ')[0]}</Text>
          <Text style={styles.teamRole}>{item.role}</Text>
          <Text style={styles.teamDepartment}>{item.department}</Text>
          <View style={styles.teamStats}>
            <View style={styles.teamStat}>
              <MaterialCommunityIcons name="clipboard-check" size={12} color={BRAND.textSecondary} />
              <Text style={styles.teamStatText}>{item.tasks}</Text>
            </View>
            <View style={styles.teamStat}>
              <MaterialCommunityIcons name="chart-line" size={12} color={BRAND.textSecondary} />
              <Text style={styles.teamStatText}>{item.performance}</Text>
            </View>
            <View style={styles.teamStat}>
              <MaterialCommunityIcons name="cash" size={12} color={BRAND.textSecondary} />
              <Text style={styles.teamStatText}>{item.utilization}</Text>
            </View>
          </View>
        </TouchableOpacity>
      )}
      contentContainerStyle={styles.teamGrid}
      showsVerticalScrollIndicator={false}
    />
  );

  // Enhanced Analytics with Business Metrics
  const renderAnalytics = () => (
    <ScrollView style={styles.scrollContent} showsVerticalScrollIndicator={false}>
      {/* Financial KPIs */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Financial Performance</Text>
        <View style={styles.kpisGrid}>
          {moduleData?.analytics?.kpis?.slice(0, 4).map((kpi, index) => (
            <View key={index} style={styles.kpiCard}>
              <View style={styles.kpiHeader}>
                <Text style={styles.kpiName}>{kpi.name}</Text>
                <MaterialCommunityIcons 
                  name={kpi.trend === 'up' ? 'trending-up' : kpi.trend === 'down' ? 'trending-down' : 'trending-neutral'} 
                  size={14} 
                  color={kpi.trend === 'up' ? BRAND.success : kpi.trend === 'down' ? BRAND.danger : BRAND.warning} 
                />
              </View>
              <View style={styles.kpiValues}>
                <Text style={styles.kpiCurrent}>{kpi.current}{kpi.unit}</Text>
                <Text style={styles.kpiTarget}>Target: {kpi.target}{kpi.unit}</Text>
              </View>
              <View style={styles.kpiProgress}>
                <View 
                  style={[
                    styles.kpiProgressFill, 
                    { 
                      width: `${Math.min((kpi.current / kpi.target) * 100, 100)}%`,
                      backgroundColor: kpi.current >= kpi.target ? BRAND.success : BRAND.warning
                    }
                  ]} 
                />
              </View>
              <Text style={[
                styles.kpiStatus,
                { color: kpi.current >= kpi.target ? BRAND.success : BRAND.warning }
              ]}>
                <Text>{kpi.current >= kpi.target ? '✓ On Target' : 'Needs Improvement'}</Text>
              </Text>
            </View>
          ))}
        </View>
      </View>

      {/* Performance Metrics Grid */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Performance Metrics</Text>
        <View style={styles.metricsGrid}>
          {Object.entries(moduleData?.analytics?.performanceMetrics || {}).map(([key, value]) => (
            <View key={key} style={styles.metricCard}>
              <View style={styles.metricHeader}>
                <Text style={styles.metricValue}>{value}%</Text>
                <MaterialCommunityIcons 
                  name={value >= 85 ? 'check-circle' : value >= 70 ? 'alert-circle' : 'close-circle'} 
                  size={16} 
                  color={value >= 85 ? BRAND.success : value >= 70 ? BRAND.warning : BRAND.danger} 
                />
              </View>
              <Text style={styles.metricLabel}>{key.charAt(0).toUpperCase() + key.slice(1)}</Text>
              <View style={styles.progressBar}>
                <View 
                  style={[
                    styles.progressFill, 
                    { 
                      width: `${value}%`,
                      backgroundColor: 
                        value >= 90 ? BRAND.success :
                        value >= 75 ? BRAND.warning : BRAND.info
                    }
                  ]} 
                />
              </View>
            </View>
          ))}
        </View>
      </View>

      {/* Category Distribution */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Budget Allocation</Text>
        <View style={styles.distributionContainer}>
          {moduleData?.analytics?.categoryDistribution?.map((category, index) => (
            <View key={index} style={styles.distributionItem}>
              <View style={[styles.distributionDot, { backgroundColor: category.color }]} />
              <View style={styles.distributionInfo}>
                <Text style={styles.distributionLabel}>{category.category}</Text>
                <Text style={styles.distributionSubtext}>Budget: {category.budget.toLocaleString()}</Text>
              </View>
              <View style={styles.distributionValues}>
                <Text style={styles.distributionValue}>{category.value}%</Text>
                <Text style={styles.distributionActual}>Actual: {category.actual.toLocaleString()}</Text>
              </View>
            </View>
          ))}
        </View>
      </View>

      {/* Financial Summary */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Financial Summary</Text>
        <View style={styles.financialGrid}>
          {Object.entries(moduleData?.analytics?.financials || {}).map(([key, value]) => (
            <View key={key} style={styles.financialCard}>
              <Text style={styles.financialValue}>{value}</Text>
              <Text style={styles.financialLabel}>{key.replace(/([A-Z])/g, ' $1')}</Text>
            </View>
          ))}
        </View>
      </View>
    </ScrollView>
  );

  // Enhanced Documents View
  const renderDocuments = () => (
    <FlatList
      data={moduleData?.documents}
      keyExtractor={item => item.id}
      renderItem={({ item }) => (
        <TouchableOpacity 
          style={styles.documentCard}
          onPress={() => Alert.alert(
            item.name,
            `Category: ${item.category}\nSize: ${item.size}\nType: ${item.type}\nUploaded: ${item.uploaded}\nBy: ${item.uploadedBy}\nVersion: ${item.version}\nAccess: ${item.accessLevel}\nDownloads: ${item.downloadCount}`,
            [
              { text: 'OK', style: 'default' },
              { text: 'Download', onPress: () => Alert.alert('Download', `Downloading ${item.name}`) },
              { text: 'Share', onPress: () => Alert.alert('Share', `Sharing ${item.name}`) },
            ]
          )}
        >
          <View style={styles.documentIcon}>
            <MaterialCommunityIcons 
              name={
                item.type === 'pdf' ? 'file-pdf' :
                item.type === 'excel' ? 'file-excel' :
                item.type === 'word' ? 'file-word' :
                item.type === 'markdown' ? 'markdown' :
                item.type === 'csv' ? 'file-delimited' : 'folder-zip'
              } 
              size={28} 
              color={module.color} 
            />
          </View>
          <View style={styles.documentInfo}>
            <Text style={styles.documentName}>{item.name}</Text>
            <View style={styles.documentMeta}>
              <View style={styles.documentMetaItem}>
                <MaterialCommunityIcons name="file" size={10} color={BRAND.textSecondary} />
                <Text style={styles.documentMetaText}>{item.size}</Text>
              </View>
              <View style={styles.documentMetaItem}>
                <MaterialCommunityIcons name="calendar" size={10} color={BRAND.textSecondary} />
                <Text style={styles.documentMetaText}>{item.uploaded}</Text>
              </View>
              <View style={styles.documentMetaItem}>
                <MaterialCommunityIcons name="shield" size={10} color={BRAND.textSecondary} />
                <Text style={styles.documentMetaText}>{item.accessLevel}</Text>
              </View>
            </View>
            <Text style={styles.documentUploader}>{item.uploadedBy} • v{item.version}</Text>
            {item.category && (
              <View style={[styles.documentCategory, { backgroundColor: `${module.color}15` }]}>
                <Text style={[styles.documentCategoryText, { color: module.color }]}>{item.category}</Text>
              </View>
            )}
          </View>
          <TouchableOpacity 
            style={styles.documentAction}
            onPress={() => Alert.alert('Download', `Downloading ${item.name}`)}
          >
            <MaterialCommunityIcons name="download" size={22} color={BRAND.primary} />
          </TouchableOpacity>
        </TouchableOpacity>
      )}
      contentContainerStyle={styles.documentsContainer}
      showsVerticalScrollIndicator={false}
    />
  );

  // Enhanced Calendar View
  const renderCalendar = () => (
    <ScrollView style={styles.scrollContent} showsVerticalScrollIndicator={false}>
      <View style={styles.sectionHeader}>
        <Text style={styles.sectionTitle}>Business Calendar</Text>
        <TouchableOpacity 
          style={styles.addEventButton}
          onPress={() => Alert.alert('Add Event', 'Event creation coming soon')}
        >
          <MaterialCommunityIcons name="plus" size={16} color="white" />
          <Text style={styles.addEventText}>Add Event</Text>
        </TouchableOpacity>
      </View>
      
      {moduleData?.calendar?.map(event => (
        <TouchableOpacity 
          key={event.id} 
          style={styles.eventCard}
          onPress={() => Alert.alert(
            event.title,
            `Date: ${event.date}\nTime: ${event.time}\nDuration: ${event.duration}\nLocation: ${event.location}\nOrganizer: ${event.organizer}\nParticipants: ${event.participants}\nType: ${event.type}\n${event.notes ? `Notes: ${event.notes}` : ''}`,
            [
              { text: 'OK', style: 'default' },
              { text: 'Join', onPress: () => Alert.alert('Join', 'Joining meeting...') },
              { text: 'Reschedule', onPress: () => Alert.alert('Reschedule', 'Reschedule functionality coming soon') },
            ]
          )}
        >
          <View style={[styles.eventIcon, { backgroundColor: `${module.color}15` }]}>
            <MaterialCommunityIcons 
              name={
                event.type === 'meeting' ? 'account-group' :
                event.type === 'review' ? 'clipboard-check' :
                event.type === 'planning' ? 'clipboard-text' :
                event.type === 'training' ? 'school' :
                event.type === 'demo' ? 'presentation' :
                event.type === 'client' ? 'account-tie' :
                event.type === 'board' ? 'chart-donut' : 'calendar'
              } 
              size={22} 
              color={module.color} 
            />
          </View>
          <View style={styles.eventInfo}>
            <Text style={styles.eventTitle}>{event.title}</Text>
            <View style={styles.eventDetails}>
              <View style={styles.eventDetail}>
                <MaterialCommunityIcons name="calendar" size={12} color={BRAND.textSecondary} />
                <Text style={styles.eventText}>{event.date} • {event.time}</Text>
              </View>
              <View style={styles.eventDetail}>
                <MaterialCommunityIcons name="map-marker" size={12} color={BRAND.textSecondary} />
                <Text style={styles.eventText}>{event.location}</Text>
              </View>
              {event.organizer && (
                <View style={styles.eventDetail}>
                  <MaterialCommunityIcons name="account" size={12} color={BRAND.textSecondary} />
                  <Text style={styles.eventText}>{event.organizer}</Text>
                </View>
              )}
            </View>
            {event.agenda && (
              <Text style={styles.eventAgenda}>{event.agenda}</Text>
            )}
          </View>
          <View style={styles.eventBadge}>
            <MaterialCommunityIcons name="account" size={14} color={BRAND.textSecondary} />
            <Text style={styles.eventBadgeText}>{event.participants}</Text>
          </View>
        </TouchableOpacity>
      ))}
    </ScrollView>
  );

  // Enhanced Integrations View
  const renderIntegrations = () => (
    <ScrollView style={styles.scrollContent} showsVerticalScrollIndicator={false}>
      <Text style={styles.sectionTitle}>Business Integrations</Text>
      <Text style={styles.sectionSubtitle}>Connect your business tools and services</Text>
      
      {moduleData?.integrations?.map((integration, index) => (
        <View key={index} style={styles.integrationCard}>
          <View style={styles.integrationHeader}>
            <View style={[styles.integrationIcon, { backgroundColor: `${module.color}15` }]}>
              <MaterialCommunityIcons name={integration.icon} size={26} color={module.color} />
            </View>
            <View style={styles.integrationInfo}>
              <Text style={styles.integrationName}>{integration.name}</Text>
              <View style={styles.integrationDetails}>
                <Text style={styles.integrationCategory}>{integration.category}</Text>
                <Text style={styles.integrationUsage}>Usage: {integration.usage}</Text>
                <Text style={styles.integrationLastUsed}>Last used: {integration.lastUsed}</Text>
              </View>
            </View>
          </View>
          <View style={styles.integrationActions}>
            <Switch
              value={integration.connected}
              onValueChange={() => Alert.alert(
                integration.connected ? 'Disconnect' : 'Connect',
                `${integration.connected ? 'Disconnect' : 'Connect'} ${integration.name}?`,
                [
                  { text: 'Cancel', style: 'cancel' },
                  { 
                    text: integration.connected ? 'Disconnect' : 'Connect', 
                    style: integration.connected ? 'destructive' : 'default',
                    onPress: () => Alert.alert(
                      'Success', 
                      `${integration.name} ${integration.connected ? 'disconnected' : 'connected'} successfully`
                    )
                  },
                ]
              )}
              trackColor={{ false: BRAND.border, true: module.color }}
              thumbColor={integration.connected ? 'white' : '#f4f3f4'}
            />
            <TouchableOpacity 
              style={styles.integrationSettings}
              onPress={() => Alert.alert('Settings', `${integration.name} settings`)}
            >
              <MaterialCommunityIcons name="cog" size={18} color={BRAND.textSecondary} />
            </TouchableOpacity>
          </View>
        </View>
      ))}
    </ScrollView>
  );

  // New Clients View
  const renderClients = () => (
    <ScrollView style={styles.scrollContent} showsVerticalScrollIndicator={false}>
      <View style={styles.sectionHeader}>
        <Text style={styles.sectionTitle}>Business Clients</Text>
        <TouchableOpacity 
          style={styles.addButton}
          onPress={() => Alert.alert('Add Client', 'Client creation coming soon')}
        >
          <MaterialCommunityIcons name="plus" size={16} color="white" />
          <Text style={styles.addButtonText}>Add Client</Text>
        </TouchableOpacity>
      </View>
      
      {moduleData?.clients?.map(client => (
        <TouchableOpacity 
          key={client.id} 
          style={styles.clientCard}
          onPress={() => Alert.alert(
            client.name,
            `Contact: ${client.contact}\nEmail: ${client.email}\nPhone: ${client.phone}\nStatus: ${client.status}\nContract Value: ${client.contractValue}\nStart Date: ${client.startDate}\nRenewal Date: ${client.renewalDate}\nSatisfaction: ${client.satisfaction}\nIndustry: ${client.industry}\nLocation: ${client.location}\nAccount Manager: ${client.accountManager}`,
            [
              { text: 'OK', style: 'default' },
              { text: 'Contact', onPress: () => Linking.openURL(`mailto:${client.email}`) },
              { text: 'View Projects', onPress: () => Alert.alert('Projects', 'Client projects view coming soon') },
            ]
          )}
        >
          <View style={[styles.clientAvatar, { backgroundColor: module.color }]}>
            <Text style={styles.clientAvatarText}>
              {client.name.split(' ')[0].charAt(0)}{client.name.split(' ')[1].charAt(0)}
            </Text>
          </View>
          <View style={styles.clientInfo}>
            <View style={styles.clientHeader}>
              <Text style={styles.clientName}>{client.name}</Text>
              <View style={[
                styles.clientStatus,
                { backgroundColor: client.status === 'active' ? BRAND.success + '20' : 
                                 client.status === 'inactive' ? BRAND.danger + '20' : BRAND.warning + '20' }
              ]}>
                <Text style={[
                  styles.clientStatusText,
                  { color: client.status === 'active' ? BRAND.success : 
                           client.status === 'inactive' ? BRAND.danger : BRAND.warning }
                ]}>
                  {String(client.status ?? '').toUpperCase()}
                </Text>
              </View>
            </View>
            <Text style={styles.clientContact}>{client.contact}</Text>
            <View style={styles.clientDetails}>
              <View style={styles.clientDetail}>
                <MaterialCommunityIcons name="cash" size={12} color={BRAND.textSecondary} />
                <Text style={styles.clientDetailText}>{client.contractValue}</Text>
              </View>
              <View style={styles.clientDetail}>
                <MaterialCommunityIcons name="chart-line" size={12} color={BRAND.textSecondary} />
                <Text style={styles.clientDetailText}>{client.satisfaction}</Text>
              </View>
              <View style={styles.clientDetail}>
                <MaterialCommunityIcons name="briefcase" size={12} color={BRAND.textSecondary} />
                <Text style={styles.clientDetailText}>{client.industry}</Text>
              </View>
            </View>
          </View>
          <MaterialCommunityIcons name="chevron-right" size={20} color={BRAND.textSecondary} />
        </TouchableOpacity>
      ))}
    </ScrollView>
  );

  // New Reports View
  const renderReports = () => (
    <ScrollView style={styles.scrollContent} showsVerticalScrollIndicator={false}>
      <View style={styles.sectionHeader}>
        <Text style={styles.sectionTitle}>Business Reports</Text>
        <TouchableOpacity 
          style={styles.addButton}
          onPress={() => Alert.alert('Generate Report', 'Report generation coming soon')}
        >
          <MaterialCommunityIcons name="plus" size={16} color="white" />
          <Text style={styles.addButtonText}>Generate</Text>
        </TouchableOpacity>
      </View>
      
      {moduleData?.reports?.map(report => (
        <TouchableOpacity 
          key={report.id} 
          style={styles.reportCard}
          onPress={() => Alert.alert(
            report.name,
            `Type: ${report.type}\nFrequency: ${report.frequency}\nLast Generated: ${report.lastGenerated}\nNext Due: ${report.nextDue}\nStatus: ${report.status}\nGenerated By: ${report.generatedBy}\nSize: ${report.size}\nFormat: ${report.format}\nRecipients: ${report.recipients}\n${report.confidential ? 'Confidential Report' : ''}`,
            [
              { text: 'OK', style: 'default' },
              { text: 'Download', onPress: () => Alert.alert('Download', `Downloading ${report.name}`) },
              { text: 'Share', onPress: () => Alert.alert('Share', `Sharing ${report.name}`) },
              { text: 'Regenerate', onPress: () => Alert.alert('Regenerate', `Regenerating ${report.name}`) },
            ]
          )}
        >
          <View style={[styles.reportIcon, { backgroundColor: `${module.color}15` }]}>
            <MaterialCommunityIcons 
              name={
                report.type === 'financial' ? 'cash' :
                report.type === 'performance' ? 'chart-bar' :
                report.type === 'audit' ? 'shield-check' :
                report.type === 'project' ? 'clipboard-check' :
                report.type === 'productivity' ? 'account-group' :
                report.type === 'survey' ? 'clipboard-text' :
                report.type === 'analysis' ? 'chart-line' : 'alert-circle'
              } 
              size={24} 
              color={module.color} 
            />
          </View>
          <View style={styles.reportInfo}>
            <View style={styles.reportHeader}>
              <Text style={styles.reportName}>{report.name}</Text>
              {report.confidential && (
                <View style={styles.confidentialBadge}>
                  <MaterialCommunityIcons name="shield" size={10} color={BRAND.danger} />
                  <Text style={styles.confidentialText}>Confidential</Text>
                </View>
              )}
            </View>
            <View style={styles.reportDetails}>
              <View style={styles.reportDetail}>
                <MaterialCommunityIcons name="calendar" size={12} color={BRAND.textSecondary} />
                <Text style={styles.reportDetailText}>{report.frequency}</Text>
              </View>
              <View style={styles.reportDetail}>
                <MaterialCommunityIcons name="file" size={12} color={BRAND.textSecondary} />
                <Text style={styles.reportDetailText}>{report.size}</Text>
              </View>
              <View style={styles.reportDetail}>
                <MaterialCommunityIcons name="account" size={12} color={BRAND.textSecondary} />
                <Text style={styles.reportDetailText}>{report.generatedBy}</Text>
              </View>
            </View>
            <View style={[
              styles.reportStatus,
              { backgroundColor: 
                report.status === 'generated' ? BRAND.success + '20' : 
                report.status === 'in-progress' ? BRAND.warning + '20' : BRAND.info + '20' }
            ]}>
              <Text style={[
                styles.reportStatusText,
                { color: 
                  report.status === 'generated' ? BRAND.success : 
                  report.status === 'in-progress' ? BRAND.warning : BRAND.info }
              ]}>
                {String(report.status ?? '').toUpperCase()}
              </Text>
            </View>
          </View>
          <MaterialCommunityIcons name="download" size={22} color={BRAND.primary} />
        </TouchableOpacity>
      ))}
    </ScrollView>
  );

  // Enhanced New Item Modal
  const renderNewModal = () => (
    <Modal 
      visible={showNewModal} 
      transparent 
      animationType="slide"
      onRequestClose={() => setShowNewModal(false)}
    >
      <View style={styles.modalOverlay}>
        <Animated.View 
          style={[
            styles.modalContent,
            { transform: [{ scale: modalScaleAnim }] }
          ]}
        >
          <View style={styles.modalHeader}>
            <View>
              <Text style={styles.modalTitle}>Create Business Item</Text>
              <Text style={styles.modalSubtitle}>Add new item to {module.name}</Text>
            </View>
            <TouchableOpacity 
              onPress={() => setShowNewModal(false)}
              style={styles.closeButton}
            >
              <MaterialCommunityIcons name="close" size={24} color={BRAND.text} />
            </TouchableOpacity>
          </View>

          <ScrollView style={styles.modalBody} showsVerticalScrollIndicator={false}>
            <Text style={styles.inputLabel}>Title *</Text>
            <TextInput
              style={styles.textInput}
              placeholder="Enter business item title"
              value={newItem.title}
              onChangeText={text => setNewItem(prev => ({ ...prev, title: text }))}
            />

            <Text style={styles.inputLabel}>Description</Text>
            <TextInput
              style={[styles.textInput, styles.textArea]}
              placeholder="Enter detailed description"
              value={newItem.description}
              onChangeText={text => setNewItem(prev => ({ ...prev, description: text }))}
              multiline
              numberOfLines={3}
            />

            <View style={styles.formRow}>
              <View style={styles.formColumn}>
                <Text style={styles.inputLabel}>Type</Text>
                <View style={styles.typeSelector}>
                  {['task', 'bug', 'feature', 'enhancement', 'project'].map(type => (
                    <TouchableOpacity
                      key={type}
                      style={[
                        styles.typeOption,
                        newItem.type === type && { backgroundColor: module.color }
                      ]}
                      onPress={() => setNewItem(prev => ({ ...prev, type }))}
                    >
                      <Text style={[
                        styles.typeOptionText,
                        newItem.type === type && { color: 'white' }
                      ]}>
                        {type}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </View>
              </View>
              
              <View style={styles.formColumn}>
                <Text style={styles.inputLabel}>Priority</Text>
                <View style={styles.prioritySelector}>
                  {['low', 'medium', 'high', 'critical'].map(priority => (
                    <TouchableOpacity
                      key={priority}
                      style={[
                        styles.priorityOption,
                        newItem.priority === priority && { 
                          backgroundColor: getPriorityColor(priority)
                        }
                      ]}
                      onPress={() => setNewItem(prev => ({ ...prev, priority }))}
                    >
                      <Text style={[
                        styles.priorityOptionText,
                        newItem.priority === priority && { color: 'white' }
                      ]}>
                        {priority}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </View>
              </View>
            </View>

            <View style={styles.formRow}>
              <View style={styles.formColumn}>
                <Text style={styles.inputLabel}>Category</Text>
                <View style={styles.categorySelector}>
                  {['sales', 'marketing', 'operations', 'finance', 'hr', 'it'].map(category => (
                    <TouchableOpacity
                      key={category}
                      style={[
                        styles.categoryOption,
                        newItem.category === category && { backgroundColor: module.color }
                      ]}
                      onPress={() => setNewItem(prev => ({ ...prev, category }))}
                    >
                      <Text style={[
                        styles.categoryOptionText,
                        newItem.category === category && { color: 'white' }
                      ]}>
                        {category}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </View>
              </View>
              
              <View style={styles.formColumn}>
                <Text style={styles.inputLabel}>Department</Text>
                <TextInput
                  style={styles.textInput}
                  placeholder="Enter department"
                  value={newItem.department}
                  onChangeText={text => setNewItem(prev => ({ ...prev, department: text }))}
                />
              </View>
            </View>

            <View style={styles.formRow}>
              <View style={styles.formColumn}>
                <Text style={styles.inputLabel}>Due Date</Text>
                <TextInput
                  style={styles.textInput}
                  placeholder="YYYY-MM-DD"
                  value={newItem.dueDate}
                  onChangeText={text => setNewItem(prev => ({ ...prev, dueDate: text }))}
                />
              </View>
              
              <View style={styles.formColumn}>
                <Text style={styles.inputLabel}>Budget ($)</Text>
                <TextInput
                  style={styles.textInput}
                  placeholder="Enter budget"
                  value={newItem.budget}
                  onChangeText={text => setNewItem(prev => ({ ...prev, budget: text }))}
                  keyboardType="numeric"
                />
              </View>
            </View>

            <Text style={styles.inputLabel}>Client</Text>
            <TextInput
              style={styles.textInput}
              placeholder="Enter client name"
              value={newItem.client}
              onChangeText={text => setNewItem(prev => ({ ...prev, client: text }))}
            />
          </ScrollView>

          <View style={styles.modalFooter}>
            <TouchableOpacity 
              style={[styles.modalButton, styles.cancelButton]}
              onPress={() => setShowNewModal(false)}
            >
              <Text style={styles.cancelButtonText}>Cancel</Text>
            </TouchableOpacity>
            <TouchableOpacity 
              style={[styles.modalButton, styles.createButton]}
              onPress={handleCreateItem}
            >
              <MaterialCommunityIcons name="check" size={18} color="white" />
              <Text style={styles.createButtonText}>Create Item</Text>
            </TouchableOpacity>
          </View>
        </Animated.View>
      </View>
    </Modal>
  );

  // Enhanced Settings Modal
  const renderSettingsModal = () => (
    <Modal 
      visible={showSettings} 
      transparent 
      animationType="slide"
      onRequestClose={() => setShowSettings(false)}
    >
      <View style={styles.modalOverlay}>
        <View style={styles.modalContent}>
          <View style={styles.modalHeader}>
            <View>
              <Text style={styles.modalTitle}>Module Settings</Text>
              <Text style={styles.modalSubtitle}>{module.name} Configuration</Text>
            </View>
            <TouchableOpacity 
              onPress={() => setShowSettings(false)}
              style={styles.closeButton}
            >
              <MaterialCommunityIcons name="close" size={24} color={BRAND.text} />
            </TouchableOpacity>
          </View>

          <ScrollView style={styles.modalBody} showsVerticalScrollIndicator={false}>
            <Text style={styles.settingsSectionTitle}>Notifications</Text>
            <View style={styles.settingGroup}>
              <View style={styles.settingItem}>
                <View style={styles.settingIcon}>
                  <MaterialCommunityIcons name="bell" size={22} color={BRAND.info} />
                </View>
                <View style={styles.settingContent}>
                  <Text style={styles.settingLabel}>Push Notifications</Text>
                  <Text style={styles.settingDescription}>Receive push notifications</Text>
                </View>
                <Switch
                  value={settings.pushNotifications}
                  onValueChange={(value) => setSettings(prev => ({ ...prev, pushNotifications: value }))}
                  trackColor={{ false: BRAND.border, true: module.color }}
                />
              </View>

              <View style={styles.settingItem}>
                <View style={styles.settingIcon}>
                  <MaterialCommunityIcons name="email" size={22} color={BRAND.primary} />
                </View>
                <View style={styles.settingContent}>
                  <Text style={styles.settingLabel}>Email Alerts</Text>
                  <Text style={styles.settingDescription}>Receive email alerts</Text>
                </View>
                <Switch
                  value={settings.emailAlerts}
                  onValueChange={(value) => setSettings(prev => ({ ...prev, emailAlerts: value }))}
                  trackColor={{ false: BRAND.border, true: module.color }}
                />
              </View>
            </View>

            <Text style={styles.settingsSectionTitle}>Security</Text>
            <View style={styles.settingGroup}>
              <View style={styles.settingItem}>
                <View style={styles.settingIcon}>
                  <MaterialCommunityIcons name="shield" size={22} color={BRAND.success} />
                </View>
                <View style={styles.settingContent}>
                  <Text style={styles.settingLabel}>Two-Factor Authentication</Text>
                  <Text style={styles.settingDescription}>Enhanced security</Text>
                </View>
                <Switch
                  value={settings.twoFactorAuth}
                  onValueChange={(value) => setSettings(prev => ({ ...prev, twoFactorAuth: value }))}
                  trackColor={{ false: BRAND.border, true: module.color }}
                />
              </View>

              <View style={styles.settingItem}>
                <View style={styles.settingIcon}>
                  <MaterialCommunityIcons name="backup-restore" size={22} color={BRAND.warning} />
                </View>
                <View style={styles.settingContent}>
                  <Text style={styles.settingLabel}>Data Backup</Text>
                  <Text style={styles.settingDescription}>Automatic backups</Text>
                </View>
                <Switch
                  value={settings.dataBackup}
                  onValueChange={(value) => setSettings(prev => ({ ...prev, dataBackup: value }))}
                  trackColor={{ false: BRAND.border, true: module.color }}
                />
              </View>

              <View style={styles.settingItem}>
                <View style={styles.settingIcon}>
                  <MaterialCommunityIcons name="clipboard-text" size={22} color={BRAND.purple} />
                </View>
                <View style={styles.settingContent}>
                  <Text style={styles.settingLabel}>Audit Log</Text>
                  <Text style={styles.settingDescription}>Track all activities</Text>
                </View>
                <Switch
                  value={settings.auditLog}
                  onValueChange={(value) => setSettings(prev => ({ ...prev, auditLog: value }))}
                  trackColor={{ false: BRAND.border, true: module.color }}
                />
              </View>
            </View>

            <Text style={styles.settingsSectionTitle}>Data Management</Text>
            <TouchableOpacity 
              style={styles.settingItem}
              onPress={() => {
                setShowSettings(false);
                setShowExport(true);
              }}
            >
              <View style={styles.settingIcon}>
                <MaterialCommunityIcons name="export" size={22} color={BRAND.accent} />
              </View>
              <View style={styles.settingContent}>
                <Text style={styles.settingLabel}>Export Format</Text>
                <Text style={styles.settingDescription}>{String(settings.exportFormat ?? '').toUpperCase()}</Text>
              </View>
              <MaterialCommunityIcons name="chevron-right" size={20} color={BRAND.textSecondary} />
            </TouchableOpacity>

            <TouchableOpacity 
              style={styles.settingItem}
              onPress={() => Alert.alert('Data Retention', 'Configure data retention policies')}
            >
              <View style={styles.settingIcon}>
                <MaterialCommunityIcons name="database" size={22} color={BRAND.info} />
              </View>
              <View style={styles.settingContent}>
                <Text style={styles.settingLabel}>Data Retention</Text>
                <Text style={styles.settingDescription}>90 days</Text>
              </View>
              <MaterialCommunityIcons name="chevron-right" size={20} color={BRAND.textSecondary} />
            </TouchableOpacity>

            <View style={styles.settingInfo}>
              <MaterialCommunityIcons name="information" size={20} color={BRAND.textSecondary} />
              <View style={styles.settingInfoContent}>
                <Text style={styles.settingInfoTitle}>Module Information</Text>
                <Text style={styles.settingInfoText}>
                  Module: {module.name}
                  {'\n'}Version: 2.1.0 Enterprise
                  {'\n'}Last Updated: {new Date().toLocaleDateString()}
                  {'\n'}Data Size: {(Math.random() * 100 + 50).toFixed(1)} MB
                  {'\n'}Items: {moduleData?.items?.length || 0}
                </Text>
              </View>
            </View>

            <TouchableOpacity 
              style={styles.dangerButton}
              onPress={() => Alert.alert(
                'Reset Settings',
                'Are you sure you want to reset all settings to default?',
                [
                  { text: 'Cancel', style: 'cancel' },
                  { 
                    text: 'Reset', 
                    style: 'destructive',
                    onPress: () => {
                      setSettings({
                        notifications: true,
                        emailAlerts: true,
                        pushNotifications: true,
                        autoSync: true,
                        darkMode: false,
                        exportFormat: 'csv',
                        twoFactorAuth: true,
                        dataBackup: true,
                        auditLog: true,
                      });
                      Alert.alert('Success', 'Settings reset to default');
                    }
                  },
                ]
              )}
            >
              <MaterialCommunityIcons name="refresh" size={18} color={BRAND.danger} />
              <Text style={styles.dangerButtonText}>Reset to Default</Text>
            </TouchableOpacity>
          </ScrollView>
        </View>
      </View>
    </Modal>
  );

  // Enhanced Export Modal
  const renderExportModal = () => (
    <Modal 
      visible={showExport} 
      transparent 
      animationType="fade"
      onRequestClose={() => setShowExport(false)}
    >
      <View style={styles.modalOverlay}>
        <View style={[styles.modalContent, styles.exportModal]}>
          <View style={styles.modalHeader}>
            <Text style={styles.modalTitle}>Export Business Data</Text>
            <TouchableOpacity onPress={() => setShowExport(false)}>
              <MaterialCommunityIcons name="close" size={24} color={BRAND.text} />
            </TouchableOpacity>
          </View>

          <View style={styles.modalBody}>
            <Text style={styles.exportDescription}>
              Export your {module.name} data for analysis, reporting, or backup purposes.
            </Text>
            
            <TouchableOpacity 
              style={styles.exportOption}
              onPress={() => handleExportData('csv')}
            >
              <LinearGradient colors={[BRAND.success, `${BRAND.success}DD`]} style={styles.exportOptionIcon}>
                <MaterialCommunityIcons name="file-delimited" size={28} color="white" />
              </LinearGradient>
              <View style={styles.exportOptionContent}>
                <Text style={styles.exportOptionTitle}>CSV Format</Text>
                <Text style={styles.exportOptionDesc}>Best for Excel, Google Sheets, and data analysis</Text>
                <Text style={styles.exportOptionStats}>{moduleData?.items?.length || 0} items available</Text>
              </View>
              <MaterialCommunityIcons name="chevron-right" size={20} color={BRAND.textSecondary} />
            </TouchableOpacity>

            <TouchableOpacity 
              style={styles.exportOption}
              onPress={() => handleExportData('json')}
            >
              <LinearGradient colors={[BRAND.primary, `${BRAND.primary}DD`]} style={styles.exportOptionIcon}>
                <MaterialCommunityIcons name="code-json" size={28} color="white" />
              </LinearGradient>
              <View style={styles.exportOptionContent}>
                <Text style={styles.exportOptionTitle}>JSON Format</Text>
                <Text style={styles.exportOptionDesc}>Best for APIs, development, and integrations</Text>
                <Text style={styles.exportOptionStats}>Full data structure with metadata</Text>
              </View>
              <MaterialCommunityIcons name="chevron-right" size={20} color={BRAND.textSecondary} />
            </TouchableOpacity>

            <TouchableOpacity 
              style={styles.exportOption}
              onPress={() => handleExportData('excel')}
            >
              <LinearGradient colors={[BRAND.accent, `${BRAND.accent}DD`]} style={styles.exportOptionIcon}>
                <MaterialCommunityIcons name="microsoft-excel" size={28} color="white" />
              </LinearGradient>
              <View style={styles.exportOptionContent}>
                <Text style={styles.exportOptionTitle}>Excel Format</Text>
                <Text style={styles.exportOptionDesc}>Ready-to-use Excel spreadsheet</Text>
                <Text style={styles.exportOptionStats}>Formatted for business reporting</Text>
              </View>
              <MaterialCommunityIcons name="chevron-right" size={20} color={BRAND.textSecondary} />
            </TouchableOpacity>

            <View style={styles.exportInfo}>
              <MaterialCommunityIcons name="information" size={16} color={BRAND.textSecondary} />
              <Text style={styles.exportInfoText}>
                Data will be copied to clipboard. For large exports, consider using the web interface.
              </Text>
            </View>
          </View>
        </View>
      </View>
    </Modal>
  );

  // Enhanced AI Chat Modal
  const renderAIModal = () => (
    <Modal 
      visible={showAI} 
      transparent 
      animationType="slide"
      onRequestClose={() => setShowAI(false)}
    >
      <View style={styles.modalOverlay}>
        <KeyboardAvoidingView 
          behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
          style={{ flex: 1 }}
        >
          <View style={styles.aiChatContainer}>
            <LinearGradient colors={BRAND.aiGradient} style={styles.aiChatHeader}>
              <View style={styles.aiChatTitleContainer}>
                <MaterialCommunityIcons name="robot" size={24} color="white" />
                <View>
                  <Text style={styles.aiChatTitle}>AI Business Assistant</Text>
                  <Text style={styles.aiChatSubtitle}>{module.name} Module • Enterprise Edition</Text>
                </View>
              </View>
              <TouchableOpacity 
                onPress={() => setShowAI(false)}
                style={styles.aiCloseButton}
              >
                <MaterialCommunityIcons name="close" size={24} color="white" />
              </TouchableOpacity>
            </LinearGradient>

            <ScrollView 
              style={styles.chatScroll} 
              contentContainerStyle={styles.chatScrollContent}
              ref={ref => {
                if (ref && aiMessages.length > 0) {
                  setTimeout(() => ref.scrollToEnd({ animated: true }), 100);
                }
              }}
            >
              {aiMessages.map((msg, index) => (
                <View 
                  key={index} 
                  style={[
                    styles.chatBubbleContainer,
                    msg.role === 'user' ? styles.userBubbleContainer : styles.aiBubbleContainer
                  ]}
                >
                  {msg.role === 'assistant' && (
                    <View style={styles.aiAvatar}>
                      <MaterialCommunityIcons name="robot" size={16} color="white" />
                    </View>
                  )}
                  <View 
                    style={[
                      styles.chatBubble, 
                      msg.role === 'user' ? styles.userBubble : styles.aiBubble
                    ]}
                  >
                    <Text style={[
                      styles.chatText,
                      msg.role === 'user' ? styles.userText : styles.aiText
                    ]}>
                      {msg.content}
                    </Text>
                    <Text style={styles.chatTime}>
                      <Text>{msg.role === 'user' ? 'You' : 'AI'} • Just now</Text>
                    </Text>
                  </View>
                  {msg.role === 'user' && (
                    <View style={styles.userAvatar}>
                      <Text style={styles.userAvatarText}>You</Text>
                    </View>
                  )}
                </View>
              ))}
            </ScrollView>

            <View style={styles.chatInputContainer}>
              <TextInput
                style={styles.chatInput}
                placeholder="Ask about business insights, analytics, or recommendations..."
                value={aiInput}
                onChangeText={setAiInput}
                placeholderTextColor={BRAND.textSecondary}
                multiline
                maxLength={500}
              />
              <TouchableOpacity 
                style={[
                  styles.sendBtn, 
                  !aiInput.trim() && styles.sendBtnDisabled
                ]} 
                onPress={handleAIMessage}
                disabled={!aiInput.trim()}
              >
                <MaterialCommunityIcons name="send" size={20} color="white" />
              </TouchableOpacity>
            </View>

            <View style={styles.chatQuickActions}>
              <Text style={styles.quickActionsTitle}>Quick Questions:</Text>
              <ScrollView horizontal showsHorizontalScrollIndicator={false}>
                <TouchableOpacity 
                  style={styles.quickAction}
                  onPress={() => {
                    setAiInput('Analyze current business performance');
                    handleAIMessage();
                  }}
                >
                  <Text style={styles.quickActionText}>Performance</Text>
                </TouchableOpacity>
                <TouchableOpacity 
                  style={styles.quickAction}
                  onPress={() => {
                    setAiInput('Recommend growth strategies');
                    handleAIMessage();
                  }}
                >
                  <Text style={styles.quickActionText}>Growth</Text>
                </TouchableOpacity>
                <TouchableOpacity 
                  style={styles.quickAction}
                  onPress={() => {
                    setAiInput('Identify risks and opportunities');
                    handleAIMessage();
                  }}
                >
                  <Text style={styles.quickActionText}>Risks</Text>
                </TouchableOpacity>
                <TouchableOpacity 
                  style={styles.quickAction}
                  onPress={() => {
                    setAiInput('Optimize resource allocation');
                    handleAIMessage();
                  }}
                >
                  <Text style={styles.quickActionText}>Resources</Text>
                </TouchableOpacity>
              </ScrollView>
            </View>
          </View>
        </KeyboardAvoidingView>
      </View>
    </Modal>
  );

  // Item Detail Modal
  const renderItemDetailModal = () => (
    <Modal 
      visible={showItemDetail} 
      transparent 
      animationType="slide"
      onRequestClose={() => setShowItemDetail(false)}
    >
      <View style={styles.modalOverlay}>
        <ScrollView style={styles.itemDetailModal}>
          <LinearGradient colors={[module.color, `${module.color}DD`]} style={styles.itemDetailHeader}>
            <View style={styles.itemDetailHeaderContent}>
              <TouchableOpacity 
                style={styles.backButtonTop}
                onPress={() => setShowItemDetail(false)}
              >
                <MaterialCommunityIcons name="arrow-left" size={24} color="white" />
                <Text style={styles.backButtonText}>Back</Text>
              </TouchableOpacity>
              <Text style={styles.itemDetailTitle}>Item Details</Text>
              <View style={{ width: 70 }} />
            </View>
          </LinearGradient>

          {selectedItem && (
            <View style={styles.itemDetailContent}>
              <View style={styles.itemDetailCard}>
                <Text style={styles.itemDetailName}>{selectedItem.title}</Text>
                <Text style={styles.itemDetailDescription}>{selectedItem.description}</Text>
                
                <View style={styles.itemDetailStatus}>
                  <View style={[styles.detailBadge, { backgroundColor: getStatusColor(selectedItem.status) }]}>
                    <Text style={styles.detailBadgeText}>{selectedItem.status}</Text>
                  </View>
                  <View style={[styles.detailBadge, { backgroundColor: getPriorityColor(selectedItem.priority) }]}>
                    <Text style={styles.detailBadgeText}>{selectedItem.priority}</Text>
                  </View>
                  <View style={[styles.detailBadge, { backgroundColor: `${module.color}20`, borderColor: module.color }]}>
                    <Text style={[styles.detailBadgeText, { color: module.color }]}>{selectedItem.category}</Text>
                  </View>
                </View>

                <View style={styles.detailSection}>
                  <Text style={styles.detailSectionTitle}>Business Information</Text>
                  <View style={styles.detailGrid}>
                    <View style={styles.detailItem}>
                      <Text style={styles.detailLabel}>Department</Text>
                      <Text style={styles.detailValue}>{selectedItem.department}</Text>
                    </View>
                    <View style={styles.detailItem}>
                      <Text style={styles.detailLabel}>Client</Text>
                      <Text style={styles.detailValue}>{selectedItem.client}</Text>
                    </View>
                    <View style={styles.detailItem}>
                      <Text style={styles.detailLabel}>Assigned To</Text>
                      <Text style={styles.detailValue}>{selectedItem.assignedTo}</Text>
                    </View>
                    <View style={styles.detailItem}>
                      <Text style={styles.detailLabel}>Due Date</Text>
                      <Text style={styles.detailValue}>{selectedItem.dueDate}</Text>
                    </View>
                  </View>
                </View>

                <View style={styles.detailSection}>
                  <Text style={styles.detailSectionTitle}>Financial Details</Text>
                  <View style={styles.financialDetails}>
                    <View style={styles.financialItem}>
                      <Text style={styles.financialLabel}>Contract Value</Text>
                      <Text style={styles.financialValue}>{selectedItem.contractValue}</Text>
                    </View>
                    <View style={styles.financialItem}>
                      <Text style={styles.financialLabel}>Budget</Text>
                      <Text style={styles.financialValue}>{selectedItem.budget}</Text>
                    </View>
                    <View style={styles.financialItem}>
                      <Text style={styles.financialLabel}>Cost</Text>
                      <Text style={styles.financialValue}>{selectedItem.cost}</Text>
                    </View>
                    <View style={styles.financialItem}>
                      <Text style={styles.financialLabel}>Value</Text>
                      <Text style={styles.financialValue}>{selectedItem.value}</Text>
                    </View>
                  </View>
                </View>

                <View style={styles.detailSection}>
                  <Text style={styles.detailSectionTitle}>Project Details</Text>
                  <View style={styles.projectDetails}>
                    <View style={styles.projectItem}>
                      <MaterialCommunityIcons name="flag" size={16} color={module.color} />
                      <Text style={styles.projectText}>Milestone: {selectedItem.milestone}</Text>
                    </View>
                    <View style={styles.projectItem}>
                      <MaterialCommunityIcons name="clock" size={16} color={module.color} />
                      <Text style={styles.projectText}>Timeline: {selectedItem.timeline}</Text>
                    </View>
                    <View style={styles.projectItem}>
                      <MaterialCommunityIcons name="alert-circle" size={16} color={module.color} />
                      <Text style={styles.projectText}>Risk Level: {selectedItem.riskLevel}</Text>
                    </View>
                    <View style={styles.projectItem}>
                      <MaterialCommunityIcons name="chart-bar" size={16} color={module.color} />
                      <Text style={styles.projectText}>Phase: {selectedItem.phase}</Text>
                    </View>
                  </View>
                </View>

                {selectedItem.tags && selectedItem.tags.length > 0 && (
                  <View style={styles.detailSection}>
                    <Text style={styles.detailSectionTitle}>Tags</Text>
                    <View style={styles.tagsContainer}>
                      {selectedItem.tags.map((tag, index) => (
                        <View key={index} style={styles.detailTag}>
                          <Text style={styles.detailTagText}>{tag}</Text>
                        </View>
                      ))}
                    </View>
                  </View>
                )}

                <View style={styles.detailActions}>
                  <TouchableOpacity 
                    style={[styles.detailActionButton, { backgroundColor: `${module.color}15` }]}
                    onPress={() => {
                      handleUpdateItemStatus(selectedItem.id, 'in-progress');
                      setShowItemDetail(false);
                    }}
                  >
                    <MaterialCommunityIcons name="play" size={16} color={module.color} />
                    <Text style={[styles.detailActionText, { color: module.color }]}>Start</Text>
                  </TouchableOpacity>
                  
                  <TouchableOpacity 
                    style={[styles.detailActionButton, { backgroundColor: `${BRAND.success}15` }]}
                    onPress={() => {
                      handleUpdateItemStatus(selectedItem.id, 'completed');
                      setShowItemDetail(false);
                    }}
                  >
                    <MaterialCommunityIcons name="check" size={16} color={BRAND.success} />
                    <Text style={[styles.detailActionText, { color: BRAND.success }]}>Complete</Text>
                  </TouchableOpacity>
                  
                  <TouchableOpacity 
                    style={[styles.detailActionButton, { backgroundColor: `${BRAND.danger}15` }]}
                    onPress={() => {
                      setShowItemDetail(false);
                      setTimeout(() => handleDeleteItem(selectedItem.id), 300);
                    }}
                  >
                    <MaterialCommunityIcons name="delete" size={16} color={BRAND.danger} />
                    <Text style={[styles.detailActionText, { color: BRAND.danger }]}>Delete</Text>
                  </TouchableOpacity>
                </View>
              </View>
            </View>
          )}
        </ScrollView>
      </View>
    </Modal>
  );

  // Helper functions
  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return BRAND.success;
      case 'in-progress': return BRAND.warning;
      case 'pending': return BRAND.info;
      case 'on-hold': return BRAND.purple;
      case 'cancelled': return BRAND.danger;
      default: return BRAND.textSecondary;
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'critical': return BRAND.danger;
      case 'high': return BRAND.warning;
      case 'medium': return BRAND.info;
      case 'low': return BRAND.success;
      default: return BRAND.textSecondary;
    }
  };

  const renderContent = () => {
    if (loading) {
      return (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={module.color} />
          <Text style={styles.loadingText}>Loading {module.name} Business Module...</Text>
          <Text style={styles.loadingSubtext}>Fetching business data and analytics</Text>
        </View>
      );
    }

    switch (activeTab) {
      case 'overview': return renderOverview();
      case 'items': return renderItems();
      case 'team': return renderTeam();
      case 'analytics': return renderAnalytics();
      case 'docs': return renderDocuments();
      case 'calendar': return renderCalendar();
      case 'integrations': return renderIntegrations();
      case 'clients': return renderClients();
      case 'reports': return renderReports();
      default: return renderOverview();
    }
  };

  if (!module) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.errorContainer}>
          <MaterialCommunityIcons name="alert-circle" size={64} color={BRAND.danger} />
          <Text style={styles.errorText}>Module Not Found</Text>
          <Text style={styles.errorSubtext}>The requested business module could not be loaded</Text>
          <TouchableOpacity 
            style={styles.backButton} 
            onPress={() => navigation.goBack()}
          >
            <MaterialCommunityIcons name="arrow-left" size={18} color="white" />
            <Text style={styles.backButtonText}>Go Back to Modules</Text>
          </TouchableOpacity>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      <StatusBar barStyle="light-content" backgroundColor={module.color} />
      
      {renderHeader()}
      {renderTabs()}

      <Animated.View 
        style={[
          styles.contentContainer, 
          { 
            opacity: fadeAnim,
            transform: [{ translateY: slideAnim }] 
          }
        ]}
      >
        {renderContent()}
      </Animated.View>

      {/* Floating Action Buttons */}
      <Animated.View style={[styles.fabAI, { transform: [{ scale: aiScaleAnim }] }]}>
        <TouchableOpacity onPress={() => setShowAI(true)} activeOpacity={0.8}>
          <LinearGradient colors={BRAND.aiGradient} style={styles.aiFab}>
            <MaterialCommunityIcons name="robot" size={24} color="white" />
          </LinearGradient>
        </TouchableOpacity>
      </Animated.View>

      <TouchableOpacity 
        style={[styles.fab, { backgroundColor: module.color }]}
        onPress={() => setShowNewModal(true)}
        activeOpacity={0.8}
      >
        <MaterialCommunityIcons name="plus" size={24} color="white" />
      </TouchableOpacity>

      {/* Optional Floating Back Button */}
      <TouchableOpacity 
        style={[styles.fab, { 
          backgroundColor: BRAND.danger,
          right: 90,
          bottom: 30 
        }]}
        onPress={handleBackPress}
        activeOpacity={0.8}
      >
        <MaterialCommunityIcons name="arrow-left" size={24} color="white" />
      </TouchableOpacity>

      {/* Modals */}
      {renderNewModal()}
      {renderSettingsModal()}
      {renderExportModal()}
      {renderAIModal()}
      {renderItemDetailModal()}
      
      {/* Additional Business Modals */}
      {showCalendar && (
        <Modal
          visible={showCalendar}
          transparent
          animationType="slide"
          onRequestClose={() => setShowCalendar(false)}
        >
          <View style={styles.modalOverlay}>
            <View style={styles.modalContent}>
              <View style={styles.modalHeader}>
                <Text style={styles.modalTitle}>Business Calendar</Text>
                <TouchableOpacity onPress={() => setShowCalendar(false)}>
                  <MaterialCommunityIcons name="close" size={24} color={BRAND.text} />
                </TouchableOpacity>
              </View>
              {renderCalendar()}
            </View>
          </View>
        </Modal>
      )}
      
      {showClients && (
        <Modal
          visible={showClients}
          transparent
          animationType="slide"
          onRequestClose={() => setShowClients(false)}
        >
          <View style={styles.modalOverlay}>
            <View style={styles.modalContent}>
              <View style={styles.modalHeader}>
                <Text style={styles.modalTitle}>Business Clients</Text>
                <TouchableOpacity onPress={() => setShowClients(false)}>
                  <MaterialCommunityIcons name="close" size={24} color={BRAND.text} />
                </TouchableOpacity>
              </View>
              {renderClients()}
            </View>
          </View>
        </Modal>
      )}
      
      {showReports && (
        <Modal
          visible={showReports}
          transparent
          animationType="slide"
          onRequestClose={() => setShowReports(false)}
        >
          <View style={styles.modalOverlay}>
            <View style={styles.modalContent}>
              <View style={styles.modalHeader}>
                <Text style={styles.modalTitle}>Business Reports</Text>
                <TouchableOpacity onPress={() => setShowReports(false)}>
                  <MaterialCommunityIcons name="close" size={24} color={BRAND.text} />
                </TouchableOpacity>
              </View>
              {renderReports()}
            </View>
          </View>
        </Modal>
      )}
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: BRAND.background },
  contentContainer: { flex: 1 },
  scrollContent: { flex: 1, paddingBottom: 20 },
  
  // Header - Enhanced
  header: { 
    paddingTop: Platform.OS === 'ios' ? 15 : 25,
    paddingBottom: 15,
    paddingHorizontal: 20,
  },
  headerContent: { 
    flexDirection: 'row', 
    alignItems: 'center',
    marginBottom: 12,
  },
  moduleIcon: {
    width: 48,
    height: 48,
    borderRadius: 14,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 14,
    borderWidth: 2,
    borderColor: 'rgba(255,255,255,0.3)',
  },
  headerText: { flex: 1 },
  headerTitle: { 
    fontSize: 24, 
    fontWeight: '800', 
    color: 'white',
    marginBottom: 4,
  },
  proBadge: {
    backgroundColor: BRAND.secondary,
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 6,
    marginLeft: 8,
  },
  proBadgeText: {
    fontSize: 10,
    fontWeight: '800',
    color: BRAND.text,
    marginLeft: 3,
  },
  headerSubtitle: { 
    fontSize: 13, 
    color: 'rgba(255,255,255,0.9)', 
    marginBottom: 6,
    lineHeight: 16,
  },
  headerMeta: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  metaItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginRight: 12,
    marginBottom: 2,
  },
  metaText: {
    fontSize: 11,
    color: 'rgba(255,255,255,0.7)',
    marginLeft: 4,
    fontWeight: '600',
  },
  quickStats: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: 'rgba(255,255,255,0.15)',
  },
  quickStat: {
    alignItems: 'center',
    flex: 1,
  },
  quickStatIcon: {
    width: 28,
    height: 28,
    borderRadius: 14,
    backgroundColor: 'rgba(255,255,255,0.15)',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 4,
  },
  quickStatText: {
    fontSize: 15,
    fontWeight: '800',
    color: 'white',
    marginBottom: 2,
  },
  quickStatLabel: {
    fontSize: 10,
    color: 'rgba(255,255,255,0.7)',
    fontWeight: '600',
  },

  // Tabs - Enhanced
  tabsContainer: { 
    backgroundColor: BRAND.surface, 
    paddingVertical: 8,
    elevation: 3,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.08,
    shadowRadius: 4,
    borderBottomWidth: 1,
    borderBottomColor: BRAND.border,
  },
  tabsContent: {
    paddingHorizontal: 12,
  },
  tab: { 
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 14, 
    paddingVertical: 6, 
    borderRadius: 18, 
    marginHorizontal: 4,
    backgroundColor: BRAND.background,
    borderWidth: 1,
    borderColor: BRAND.border,
    height: 34,
    position: 'relative',
  },
  activeTab: { 
    backgroundColor: BRAND.primary,
    borderColor: BRAND.primary,
  },
  tabText: { 
    fontSize: 12, 
    fontWeight: '600', 
    color: BRAND.textSecondary,
    marginLeft: 5,
  },
  activeTabText: { color: 'white' },
  tabBadge: {
    position: 'absolute',
    top: -6,
    right: -6,
    minWidth: 18,
    height: 18,
    borderRadius: 9,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 4,
  },
  tabBadgeText: {
    fontSize: 9,
    fontWeight: '800',
  },

  // AI Insight Card - Enhanced
  aiInsightCard: {
    margin: 15,
    padding: 16,
    borderRadius: 14,
    elevation: 2,
  },
  aiInsightHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 10,
  },
  aiInsightTitle: {
    color: 'white',
    fontWeight: '800',
    fontSize: 15,
    marginLeft: 8,
    flex: 1,
  },
  aiInsightButton: {
    padding: 4,
  },
  aiInsightText: {
    color: 'rgba(255,255,255,0.95)',
    fontSize: 13,
    lineHeight: 18,
    marginBottom: 10,
  },
  aiInsightFooter: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderTopWidth: 1,
    borderTopColor: 'rgba(255,255,255,0.2)',
    paddingTop: 10,
  },
  aiInsightTimestamp: {
    color: 'rgba(255,255,255,0.7)',
    fontSize: 11,
  },
  aiInsightAction: {
    color: 'white',
    fontSize: 12,
    fontWeight: '600',
    textDecorationLine: 'underline',
  },

  // Stats Grid - Enhanced
  statsGrid: { 
    flexDirection: 'row', 
    flexWrap: 'wrap', 
    paddingHorizontal: 15,
    marginBottom: 10,
  },
  statCard: { 
    width: '48%', 
    backgroundColor: BRAND.surface, 
    marginHorizontal: '1%', 
    marginBottom: 10,
    padding: 14, 
    borderRadius: 12,
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    alignItems: 'flex-start',
  },
  statValue: { 
    fontSize: 18, 
    fontWeight: '800', 
    color: BRAND.text,
    marginBottom: 4,
  },
  statLabel: { 
    fontSize: 11, 
    color: BRAND.textSecondary, 
    textTransform: 'uppercase',
    fontWeight: '600',
    marginBottom: 8,
  },
  statTrend: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  statTrendText: {
    fontSize: 11,
    fontWeight: '600',
    marginLeft: 3,
  },

  // Sections - Enhanced
  section: {
    paddingHorizontal: 15,
    marginTop: 15,
  },
  sectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 14,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: '800',
    color: BRAND.text,
  },
  sectionSubtitle: {
    fontSize: 13,
    color: BRAND.textSecondary,
    marginTop: 2,
    marginBottom: 15,
  },
  viewAllText: {
    fontSize: 13,
    color: BRAND.primary,
    fontWeight: '600',
  },

  // Actions Grid - Enhanced
  actionsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
  },
  actionCard: {
    width: '31%',
    alignItems: 'center',
    marginBottom: 15,
  },
  actionIcon: {
    width: 56,
    height: 56,
    borderRadius: 16,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 8,
    elevation: 2,
  },
  actionText: {
    fontSize: 12,
    fontWeight: '600',
    color: BRAND.text,
    textAlign: 'center',
  },

  // Activities - Enhanced
  activitiesList: {
    backgroundColor: BRAND.surface,
    borderRadius: 12,
    padding: 14,
    elevation: 2,
  },
  activityItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: BRAND.border,
  },
  activityAvatar: {
    width: 32,
    height: 32,
    borderRadius: 16,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  activityAvatarText: {
    fontSize: 11,
    fontWeight: '800',
  },
  activityContent: {
    flex: 1,
  },
  activityText: {
    fontSize: 13,
    fontWeight: '600',
    color: BRAND.text,
    marginBottom: 4,
    lineHeight: 16,
  },
  activityMeta: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  activityUser: {
    fontSize: 11,
    color: BRAND.textSecondary,
    marginRight: 8,
  },
  activityTime: {
    fontSize: 11,
    color: BRAND.textSecondary,
  },
  activityBadge: {
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 10,
    marginLeft: 8,
  },
  activityBadgeText: {
    fontSize: 10,
    fontWeight: '700',
  },

  // Events Preview
  eventsPreview: {
    backgroundColor: BRAND.surface,
    borderRadius: 12,
    padding: 14,
    elevation: 2,
  },
  eventPreviewCard: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: BRAND.border,
  },
  eventPreviewIcon: {
    width: 36,
    height: 36,
    borderRadius: 10,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  eventPreviewInfo: {
    flex: 1,
  },
  eventPreviewTitle: {
    fontSize: 13,
    fontWeight: '600',
    color: BRAND.text,
    marginBottom: 2,
  },
  eventPreviewDetails: {
    fontSize: 11,
    color: BRAND.textSecondary,
  },

  // Search & Filter - Enhanced
  searchSection: {
    flexDirection: 'row',
    padding: 12,
    paddingBottom: 8,
    backgroundColor: BRAND.surface,
    borderBottomWidth: 1,
    borderBottomColor: BRAND.border,
  },
  searchBar: {
    flex: 1,
    flexDirection: 'row',
    backgroundColor: BRAND.background,
    borderRadius: 10,
    paddingHorizontal: 14,
    height: 40,
    alignItems: 'center',
    elevation: 1,
    borderWidth: 1,
    borderColor: BRAND.border,
  },
  searchInput: {
    flex: 1,
    marginLeft: 10,
    fontSize: 14,
    color: BRAND.text,
    height: 40,
  },
  filterButton: {
    width: 40,
    height: 40,
    backgroundColor: BRAND.surface,
    borderRadius: 10,
    justifyContent: 'center',
    alignItems: 'center',
    marginLeft: 10,
    elevation: 1,
    borderWidth: 1,
    borderColor: BRAND.border,
    position: 'relative',
  },
  filterIndicator: {
    position: 'absolute',
    top: 4,
    right: 4,
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: BRAND.primary,
  },
  filtersPanel: {
    backgroundColor: BRAND.surface,
    padding: 16,
    marginHorizontal: 12,
    borderRadius: 12,
    marginBottom: 8,
    elevation: 3,
    borderWidth: 1,
    borderColor: BRAND.border,
  },
  filterHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  filterTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: BRAND.text,
  },
  clearFiltersButton: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    backgroundColor: BRAND.background,
    borderRadius: 8,
  },
  clearFiltersText: {
    fontSize: 12,
    color: BRAND.primary,
    fontWeight: '600',
  },
  filterRow: {
    marginBottom: 12,
  },
  filterLabel: {
    fontSize: 13,
    fontWeight: '600',
    color: BRAND.text,
    marginBottom: 8,
  },
  filterChips: {
    flexDirection: 'row',
  },
  filterChip: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    backgroundColor: BRAND.background,
    borderRadius: 20,
    marginRight: 8,
    borderWidth: 1,
    borderColor: BRAND.border,
  },
  activeFilterChip: {
    backgroundColor: BRAND.primary,
    borderColor: BRAND.primary,
  },
  filterChipText: {
    fontSize: 12,
    color: BRAND.text,
    fontWeight: '600',
  },
  activeFilterChipText: {
    color: 'white',
  },

  // Items - Enhanced
  itemCard: {
    backgroundColor: BRAND.surface,
    padding: 16,
    borderRadius: 12,
    marginHorizontal: 12,
    marginBottom: 10,
    elevation: 2,
    borderWidth: 1,
    borderColor: BRAND.border,
  },
  itemHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 10,
  },
  statusBadge: {
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 10,
    marginRight: 6,
  },
  statusText: {
    fontSize: 10,
    color: 'white',
    fontWeight: '800',
    textTransform: 'uppercase',
  },
  priorityBadge: {
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 10,
    marginRight: 6,
  },
  priorityText: {
    fontSize: 10,
    color: 'white',
    fontWeight: '800',
    textTransform: 'uppercase',
  },
  categoryBadge: {
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 10,
    borderWidth: 1,
  },
  categoryText: {
    fontSize: 10,
    fontWeight: '800',
    textTransform: 'uppercase',
  },
  itemMenuButton: {
    padding: 4,
  },
  itemTitle: {
    fontSize: 16,
    fontWeight: '800',
    color: BRAND.text,
    marginBottom: 6,
    lineHeight: 20,
  },
  itemDescription: {
    fontSize: 13,
    color: BRAND.textSecondary,
    marginBottom: 12,
    lineHeight: 18,
  },
  itemMeta: {
    marginBottom: 10,
  },
  metaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 6,
  },
  metaItemSmall: {
    flexDirection: 'row',
    alignItems: 'center',
    marginRight: 12,
  },
  metaTextSmall: {
    fontSize: 11,
    color: BRAND.textSecondary,
    marginLeft: 4,
  },
  itemTags: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginBottom: 12,
  },
  tag: {
    backgroundColor: BRAND.background,
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 8,
    marginRight: 6,
    marginBottom: 6,
  },
  tagText: {
    fontSize: 11,
    color: BRAND.textSecondary,
    fontWeight: '600',
  },
  itemFooter: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 8,
  },
  itemFinancial: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  financialItem: {
    marginRight: 16,
  },
  financialLabel: {
    fontSize: 11,
    color: BRAND.textSecondary,
    marginBottom: 2,
  },
  financialValue: {
    fontSize: 14,
    fontWeight: '700',
    color: BRAND.text,
  },
  itemActions: {
    flexDirection: 'row',
  },
  itemActionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 8,
    marginLeft: 8,
  },
  itemActionText: {
    fontSize: 11,
    fontWeight: '600',
    marginLeft: 4,
  },

  // Team - Enhanced
  teamGrid: {
    padding: 12,
  },
  teamCard: {
    width: '48%',
    backgroundColor: BRAND.surface,
    margin: '1%',
    padding: 14,
    borderRadius: 12,
    elevation: 2,
    borderWidth: 1,
    borderColor: BRAND.border,
  },
  teamCardHeader: {
    position: 'relative',
    alignItems: 'center',
    marginBottom: 10,
  },
  avatar: {
    width: 56,
    height: 56,
    borderRadius: 28,
    justifyContent: 'center',
    alignItems: 'center',
  },
  avatarText: {
    color: 'white',
    fontSize: 18,
    fontWeight: '800',
  },
  statusDot: {
    position: 'absolute',
    bottom: 2,
    right: 2,
    width: 12,
    height: 12,
    borderRadius: 6,
    borderWidth: 2,
    borderColor: BRAND.surface,
  },
  teamName: {
    fontSize: 13,
    fontWeight: '800',
    color: BRAND.text,
    textAlign: 'center',
    marginBottom: 2,
  },
  teamRole: {
    fontSize: 12,
    color: BRAND.text,
    textAlign: 'center',
    marginBottom: 2,
    fontWeight: '600',
  },
  teamDepartment: {
    fontSize: 11,
    color: BRAND.textSecondary,
    textAlign: 'center',
    marginBottom: 10,
  },
  teamStats: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    borderTopWidth: 1,
    borderTopColor: BRAND.border,
    paddingTop: 10,
  },
  teamStat: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  teamStatText: {
    fontSize: 12,
    color: BRAND.textSecondary,
    marginLeft: 3,
  },

  // Analytics - Enhanced
  kpisGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginBottom: 10,
  },
  kpiCard: {
    width: '48%',
    backgroundColor: BRAND.surface,
    marginHorizontal: '1%',
    marginBottom: 10,
    padding: 14,
    borderRadius: 12,
    elevation: 2,
    borderWidth: 1,
    borderColor: BRAND.border,
  },
  kpiHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  kpiName: {
    fontSize: 13,
    fontWeight: '800',
    color: BRAND.text,
    flex: 1,
    marginRight: 8,
  },
  kpiValues: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    marginBottom: 8,
  },
  kpiCurrent: {
    fontSize: 20,
    fontWeight: '800',
    color: BRAND.text,
  },
  kpiTarget: {
    fontSize: 12,
    color: BRAND.textSecondary,
  },
  kpiProgress: {
    height: 6,
    backgroundColor: BRAND.background,
    borderRadius: 3,
    marginBottom: 8,
    overflow: 'hidden',
  },
  kpiProgressFill: {
    height: '100%',
    borderRadius: 3,
  },
  kpiStatus: {
    fontSize: 11,
    fontWeight: '700',
  },
  metricsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  metricCard: {
    width: '48%',
    backgroundColor: BRAND.surface,
    margin: '1%',
    padding: 14,
    borderRadius: 12,
    elevation: 2,
    borderWidth: 1,
    borderColor: BRAND.border,
  },
  metricHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 6,
  },
  metricValue: {
    fontSize: 22,
    fontWeight: '800',
    color: BRAND.text,
  },
  metricLabel: {
    fontSize: 12,
    color: BRAND.textSecondary,
    marginBottom: 10,
  },
  progressBar: {
    height: 6,
    backgroundColor: BRAND.background,
    borderRadius: 3,
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    borderRadius: 3,
  },
  distributionContainer: {
    backgroundColor: BRAND.surface,
    borderRadius: 12,
    padding: 14,
    elevation: 2,
    borderWidth: 1,
    borderColor: BRAND.border,
  },
  distributionItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: BRAND.border,
  },
  distributionDot: {
    width: 12,
    height: 12,
    borderRadius: 6,
    marginRight: 10,
  },
  distributionInfo: {
    flex: 1,
  },
  distributionLabel: {
    fontSize: 13,
    fontWeight: '600',
    color: BRAND.text,
    marginBottom: 2,
  },
  distributionSubtext: {
    fontSize: 11,
    color: BRAND.textSecondary,
  },
  distributionValues: {
    alignItems: 'flex-end',
  },
  distributionValue: {
    fontSize: 14,
    fontWeight: '800',
    color: BRAND.text,
  },
  distributionActual: {
    fontSize: 11,
    color: BRAND.textSecondary,
    marginTop: 2,
  },
  financialGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  financialCard: {
    width: '48%',
    backgroundColor: BRAND.surface,
    marginHorizontal: '1%',
    marginBottom: 10,
    padding: 14,
    borderRadius: 12,
    elevation: 2,
    borderWidth: 1,
    borderColor: BRAND.border,
    alignItems: 'center',
  },
  financialValue: {
    fontSize: 18,
    fontWeight: '800',
    color: BRAND.text,
    marginBottom: 4,
  },
  financialLabel: {
    fontSize: 11,
    color: BRAND.textSecondary,
    textAlign: 'center',
    textTransform: 'uppercase',
    fontWeight: '600',
  },

  // Documents - Enhanced
  documentsContainer: {
    padding: 12,
  },
  documentCard: {
    flexDirection: 'row',
    backgroundColor: BRAND.surface,
    padding: 14,
    borderRadius: 12,
    marginBottom: 10,
    alignItems: 'center',
    elevation: 2,
    borderWidth: 1,
    borderColor: BRAND.border,
  },
  documentIcon: {
    width: 42,
    height: 42,
    borderRadius: 10,
    backgroundColor: BRAND.background,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 14,
  },
  documentInfo: {
    flex: 1,
  },
  documentName: {
    fontSize: 14,
    fontWeight: '600',
    color: BRAND.text,
    marginBottom: 6,
    lineHeight: 18,
  },
  documentMeta: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 6,
    flexWrap: 'wrap',
  },
  documentMetaItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginRight: 12,
    marginBottom: 2,
  },
  documentMetaText: {
    fontSize: 11,
    color: BRAND.textSecondary,
    marginLeft: 4,
  },
  documentUploader: {
    fontSize: 11,
    color: BRAND.textSecondary,
    marginBottom: 6,
  },
  documentCategory: {
    alignSelf: 'flex-start',
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 6,
  },
  documentCategoryText: {
    fontSize: 10,
    fontWeight: '600',
  },
  documentAction: {
    padding: 6,
  },

  // Calendar - Enhanced
  eventCard: {
    flexDirection: 'row',
    backgroundColor: BRAND.surface,
    padding: 14,
    borderRadius: 12,
    marginHorizontal: 12,
    marginBottom: 10,
    alignItems: 'center',
    elevation: 2,
    borderWidth: 1,
    borderColor: BRAND.border,
  },
  eventIcon: {
    width: 42,
    height: 42,
    borderRadius: 10,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 14,
  },
  eventInfo: {
    flex: 1,
  },
  eventTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: BRAND.text,
    marginBottom: 6,
  },
  eventDetails: {
    marginBottom: 6,
  },
  eventDetail: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 3,
  },
  eventText: {
    fontSize: 12,
    color: BRAND.textSecondary,
    marginLeft: 6,
  },
  eventAgenda: {
    fontSize: 12,
    color: BRAND.text,
    fontStyle: 'italic',
    marginTop: 4,
    lineHeight: 16,
  },
  eventBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: BRAND.background,
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 10,
  },
  eventBadgeText: {
    fontSize: 12,
    color: BRAND.textSecondary,
    marginLeft: 3,
    fontWeight: '600',
  },

  // Integrations - Enhanced
  integrationCard: {
    flexDirection: 'row',
    backgroundColor: BRAND.surface,
    padding: 14,
    borderRadius: 12,
    marginHorizontal: 12,
    marginBottom: 10,
    alignItems: 'center',
    justifyContent: 'space-between',
    elevation: 2,
    borderWidth: 1,
    borderColor: BRAND.border,
  },
  integrationHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  integrationIcon: {
    width: 44,
    height: 44,
    borderRadius: 10,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 14,
  },
  integrationInfo: {
    flex: 1,
  },
  integrationName: {
    fontSize: 14,
    fontWeight: '600',
    color: BRAND.text,
    marginBottom: 4,
  },
  integrationDetails: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  integrationCategory: {
    fontSize: 11,
    color: BRAND.primary,
    fontWeight: '600',
    marginRight: 10,
  },
  integrationUsage: {
    fontSize: 11,
    color: BRAND.textSecondary,
    marginRight: 10,
  },
  integrationLastUsed: {
    fontSize: 11,
    color: BRAND.textSecondary,
  },
  integrationActions: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  integrationSettings: {
    padding: 6,
    marginLeft: 8,
  },

  // Clients - Enhanced
  clientCard: {
    flexDirection: 'row',
    backgroundColor: BRAND.surface,
    padding: 14,
    borderRadius: 12,
    marginHorizontal: 12,
    marginBottom: 10,
    alignItems: 'center',
    elevation: 2,
    borderWidth: 1,
    borderColor: BRAND.border,
  },
  clientAvatar: {
    width: 44,
    height: 44,
    borderRadius: 22,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 14,
  },
  clientAvatarText: {
    color: 'white',
    fontSize: 16,
    fontWeight: '800',
  },
  clientInfo: {
    flex: 1,
  },
  clientHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 4,
  },
  clientName: {
    fontSize: 14,
    fontWeight: '600',
    color: BRAND.text,
    flex: 1,
    marginRight: 8,
  },
  clientStatus: {
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 10,
  },
  clientStatusText: {
    fontSize: 10,
    fontWeight: '700',
  },
  clientContact: {
    fontSize: 12,
    color: BRAND.textSecondary,
    marginBottom: 6,
  },
  clientDetails: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  clientDetail: {
    flexDirection: 'row',
    alignItems: 'center',
    marginRight: 12,
    marginBottom: 2,
  },
  clientDetailText: {
    fontSize: 11,
    color: BRAND.textSecondary,
    marginLeft: 4,
  },

  // Reports - Enhanced
  reportCard: {
    flexDirection: 'row',
    backgroundColor: BRAND.surface,
    padding: 14,
    borderRadius: 12,
    marginHorizontal: 12,
    marginBottom: 10,
    alignItems: 'center',
    elevation: 2,
    borderWidth: 1,
    borderColor: BRAND.border,
  },
  reportIcon: {
    width: 44,
    height: 44,
    borderRadius: 10,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 14,
  },
  reportInfo: {
    flex: 1,
  },
  reportHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 6,
  },
  reportName: {
    fontSize: 14,
    fontWeight: '600',
    color: BRAND.text,
    flex: 1,
    marginRight: 8,
  },
  confidentialBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: `${BRAND.danger}15`,
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 8,
  },
  confidentialText: {
    fontSize: 9,
    color: BRAND.danger,
    fontWeight: '700',
    marginLeft: 3,
  },
  reportDetails: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginBottom: 6,
  },
  reportDetail: {
    flexDirection: 'row',
    alignItems: 'center',
    marginRight: 12,
    marginBottom: 2,
  },
  reportDetailText: {
    fontSize: 11,
    color: BRAND.textSecondary,
    marginLeft: 4,
  },
  reportStatus: {
    alignSelf: 'flex-start',
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 10,
  },
  reportStatusText: {
    fontSize: 10,
    fontWeight: '700',
  },

  // Empty State - Enhanced
  emptyState: {
    paddingVertical: 60,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 40,
  },
  emptyStateText: {
    fontSize: 16,
    fontWeight: '700',
    color: BRAND.text,
    marginTop: 16,
    textAlign: 'center',
  },
  emptyStateSubtext: {
    fontSize: 13,
    color: BRAND.textSecondary,
    marginTop: 6,
    textAlign: 'center',
    marginBottom: 24,
  },
  emptyStateButton: {
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderRadius: 10,
    flexDirection: 'row',
    alignItems: 'center',
  },
  emptyStateButtonText: {
    color: 'white',
    fontSize: 14,
    fontWeight: '600',
    marginLeft: 6,
  },

  // Error & Loading - Enhanced
  errorContainer: { 
    flex: 1, 
    justifyContent: 'center', 
    alignItems: 'center', 
    padding: 40,
    backgroundColor: BRAND.background,
  },
  errorText: { 
    fontSize: 20, 
    fontWeight: '800', 
    color: BRAND.text, 
    marginTop: 20, 
    marginBottom: 8,
  },
  errorSubtext: { 
    fontSize: 14, 
    color: BRAND.textSecondary, 
    textAlign: 'center',
    marginBottom: 30,
    lineHeight: 20,
  },
  backButton: { 
    backgroundColor: BRAND.primary, 
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 24, 
    paddingVertical: 12, 
    borderRadius: 10,
    elevation: 2,
  },
  backButtonText: { 
    color: 'white', 
    fontSize: 15, 
    fontWeight: '600',
    marginLeft: 6,
  },
  loadingContainer: { 
    flex: 1, 
    justifyContent: 'center', 
    alignItems: 'center',
    backgroundColor: BRAND.background,
  },
  loadingText: { 
    marginTop: 20, 
    fontSize: 16, 
    color: BRAND.text,
    fontWeight: '700',
  },
  loadingSubtext: { 
    marginTop: 8, 
    fontSize: 13, 
    color: BRAND.textSecondary,
  },

  // Modals - Enhanced
  modalOverlay: { 
    flex: 1, 
    backgroundColor: 'rgba(0,0,0,0.6)', 
    justifyContent: 'center', 
    alignItems: 'center',
    padding: 20,
  },
  modalContent: { 
    backgroundColor: BRAND.surface, 
    borderRadius: 20, 
    width: '100%',
    maxHeight: '85%',
    elevation: 10,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
  },
  exportModal: {
    maxWidth: 500,
  },
  modalHeader: { 
    flexDirection: 'row', 
    justifyContent: 'space-between', 
    alignItems: 'center',
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: BRAND.border,
  },
  closeButton: {
    padding: 4,
  },
  modalTitle: { 
    fontSize: 20, 
    fontWeight: '800', 
    color: BRAND.text,
  },
  modalSubtitle: { 
    fontSize: 13, 
    color: BRAND.textSecondary,
    marginTop: 2,
  },
  modalBody: { 
    padding: 20,
    maxHeight: height * 0.6,
  },
  modalFooter: { 
    flexDirection: 'row', 
    padding: 20,
    borderTopWidth: 1,
    borderTopColor: BRAND.border,
  },
  modalButton: { 
    flex: 1, 
    padding: 14, 
    borderRadius: 10,
    marginHorizontal: 8,
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'row',
  },
  cancelButton: {
    backgroundColor: BRAND.background,
    borderWidth: 1,
    borderColor: BRAND.border,
  },
  cancelButtonText: { 
    fontSize: 15, 
    fontWeight: '600', 
    color: BRAND.text,
  },
  createButton: {
    backgroundColor: BRAND.primary,
  },
  createButtonText: { 
    fontSize: 15, 
    fontWeight: '600', 
    color: 'white',
    marginLeft: 6,
  },

  // Form Elements - Enhanced
  inputLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: BRAND.text,
    marginBottom: 8,
    marginTop: 16,
  },
  textInput: {
    backgroundColor: BRAND.background,
    padding: 12,
    borderRadius: 10,
    fontSize: 15,
    color: BRAND.text,
    borderWidth: 1,
    borderColor: BRAND.border,
  },
  textArea: {
    height: 100,
    textAlignVertical: 'top',
  },
  formRow: {
    flexDirection: 'row',
    marginHorizontal: -5,
    marginTop: 10,
  },
  formColumn: {
    flex: 1,
    marginHorizontal: 5,
  },
  typeSelector: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginTop: 6,
  },
  typeOption: {
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: 8,
    marginRight: 8,
    marginBottom: 8,
    backgroundColor: BRAND.background,
    borderWidth: 1,
    borderColor: BRAND.border,
  },
  typeOptionText: {
    fontSize: 13,
    fontWeight: '600',
    color: BRAND.text,
  },
  prioritySelector: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginTop: 6,
  },
  priorityOption: {
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: 8,
    marginRight: 8,
    marginBottom: 8,
    backgroundColor: BRAND.background,
    borderWidth: 1,
    borderColor: BRAND.border,
  },
  priorityOptionText: {
    fontSize: 13,
    fontWeight: '600',
    color: BRAND.text,
  },
  categorySelector: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginTop: 6,
  },
  categoryOption: {
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: 8,
    marginRight: 8,
    marginBottom: 8,
    backgroundColor: BRAND.background,
    borderWidth: 1,
    borderColor: BRAND.border,
  },
  categoryOptionText: {
    fontSize: 13,
    fontWeight: '600',
    color: BRAND.text,
  },

  // Export Modal - Enhanced
  exportDescription: { 
    fontSize: 14, 
    color: BRAND.textSecondary, 
    marginBottom: 20,
    lineHeight: 20,
  },
  exportOption: { 
    flexDirection: 'row', 
    alignItems: 'center', 
    padding: 16, 
    backgroundColor: BRAND.background,
    borderRadius: 12,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: BRAND.border,
  },
  exportOptionIcon: {
    width: 52,
    height: 52,
    borderRadius: 12,
    justifyContent: 'center',
    alignItems: 'center',
  },
  exportOptionContent: { 
    flex: 1, 
    marginLeft: 16,
  },
  exportOptionTitle: { 
    fontSize: 16, 
    fontWeight: '700', 
    color: BRAND.text,
    marginBottom: 4,
  },
  exportOptionDesc: { 
    fontSize: 13, 
    color: BRAND.textSecondary, 
    marginBottom: 4,
    lineHeight: 16,
  },
  exportOptionStats: { 
    fontSize: 12, 
    color: BRAND.primary,
    fontWeight: '600',
  },
  exportInfo: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    backgroundColor: `${BRAND.info}10`,
    padding: 12,
    borderRadius: 10,
    marginTop: 16,
    borderWidth: 1,
    borderColor: `${BRAND.info}30`,
  },
  exportInfoText: {
    fontSize: 12,
    color: BRAND.textSecondary,
    marginLeft: 10,
    flex: 1,
    lineHeight: 16,
  },

  // Settings - Enhanced
  settingsSectionTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: BRAND.text,
    marginTop: 20,
    marginBottom: 12,
  },
  settingGroup: {
    backgroundColor: BRAND.background,
    borderRadius: 12,
    padding: 4,
    marginBottom: 16,
    borderWidth: 1,
    borderColor: BRAND.border,
  },
  settingItem: { 
    flexDirection: 'row', 
    alignItems: 'center', 
    paddingVertical: 14,
    paddingHorizontal: 12,
    borderBottomWidth: 1,
    borderBottomColor: BRAND.border,
  },
  settingIcon: {
    width: 40,
    height: 40,
    borderRadius: 10,
    backgroundColor: BRAND.background,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 14,
  },
  settingContent: { 
    flex: 1, 
  },
  settingLabel: { 
    fontSize: 15, 
    fontWeight: '600', 
    color: BRAND.text,
    marginBottom: 2,
  },
  settingDescription: { 
    fontSize: 12, 
    color: BRAND.textSecondary,
  },
  settingInfo: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    backgroundColor: BRAND.background,
    padding: 16,
    borderRadius: 12,
    marginTop: 20,
    borderWidth: 1,
    borderColor: BRAND.border,
  },
  settingInfoContent: {
    marginLeft: 14,
    flex: 1,
  },
  settingInfoTitle: {
    fontSize: 14,
    fontWeight: '700',
    color: BRAND.text,
    marginBottom: 6,
  },
  settingInfoText: {
    fontSize: 13,
    color: BRAND.textSecondary,
    lineHeight: 18,
  },
  dangerButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: `${BRAND.danger}10`,
    padding: 16,
    borderRadius: 12,
    marginTop: 20,
    borderWidth: 1,
    borderColor: `${BRAND.danger}30`,
  },
  dangerButtonText: {
    fontSize: 15,
    color: BRAND.danger,
    fontWeight: '600',
    marginLeft: 8,
  },

  // AI Chat - Enhanced
  aiChatContainer: {
    flex: 1,
    backgroundColor: BRAND.surface,
    marginTop: 40,
    borderTopLeftRadius: 24,
    borderTopRightRadius: 24,
    overflow: 'hidden',
    maxHeight: '90%',
    elevation: 10,
  },
  aiChatHeader: {
    padding: 20,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  aiChatTitleContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  aiChatTitle: {
    color: 'white',
    fontSize: 20,
    fontWeight: '800',
    marginLeft: 12,
  },
  aiChatSubtitle: {
    color: 'rgba(255,255,255,0.8)',
    fontSize: 13,
    marginTop: 2,
    marginLeft: 12,
  },
  aiCloseButton: {
    padding: 4,
  },
  chatScroll: {
    flex: 1,
    backgroundColor: BRAND.background,
  },
  chatScrollContent: {
    padding: 20,
    paddingBottom: 40,
  },
  chatBubbleContainer: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 16,
  },
  userBubbleContainer: {
    justifyContent: 'flex-end',
  },
  aiBubbleContainer: {
    justifyContent: 'flex-start',
  },
  aiAvatar: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: BRAND.aiGradient[0],
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 10,
  },
  userAvatar: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: BRAND.primary,
    justifyContent: 'center',
    alignItems: 'center',
    marginLeft: 10,
  },
  userAvatarText: {
    color: 'white',
    fontSize: 12,
    fontWeight: '800',
  },
  chatBubble: {
    padding: 14,
    borderRadius: 18,
    maxWidth: '80%',
  },
  userBubble: {
    backgroundColor: BRAND.primary,
    borderBottomRightRadius: 6,
  },
  aiBubble: {
    backgroundColor: BRAND.surface,
    borderBottomLeftRadius: 6,
    borderWidth: 1,
    borderColor: BRAND.border,
    elevation: 1,
  },
  chatText: {
    fontSize: 15,
    lineHeight: 20,
  },
  userText: {
    color: 'white',
  },
  aiText: {
    color: BRAND.text,
  },
  chatTime: {
    fontSize: 11,
    color: BRAND.textSecondary,
    marginTop: 6,
  },
  chatInputContainer: {
    flexDirection: 'row',
    padding: 16,
    backgroundColor: BRAND.surface,
    borderTopWidth: 1,
    borderTopColor: BRAND.border,
    alignItems: 'center',
  },
  chatInput: {
    flex: 1,
    backgroundColor: BRAND.background,
    minHeight: 44,
    maxHeight: 100,
    borderRadius: 22,
    paddingHorizontal: 18,
    paddingVertical: 10,
    marginRight: 12,
    fontSize: 15,
    color: BRAND.text,
    borderWidth: 1,
    borderColor: BRAND.border,
  },
  sendBtn: {
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: BRAND.primary,
    justifyContent: 'center',
    alignItems: 'center',
  },
  sendBtnDisabled: {
    backgroundColor: BRAND.border,
  },
  chatQuickActions: {
    padding: 16,
    backgroundColor: BRAND.background,
    borderTopWidth: 1,
    borderTopColor: BRAND.border,
  },
  quickActionsTitle: {
    fontSize: 13,
    fontWeight: '600',
    color: BRAND.text,
    marginBottom: 10,
  },
  quickAction: {
    backgroundColor: BRAND.surface,
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: 20,
    marginRight: 10,
    borderWidth: 1,
    borderColor: BRAND.border,
  },
  quickActionText: {
    fontSize: 13,
    color: BRAND.text,
    fontWeight: '600',
  },

  // Item Detail Modal
  itemDetailModal: {
    flex: 1,
    backgroundColor: BRAND.background,
  },
  itemDetailHeader: {
    paddingTop: Platform.OS === 'ios' ? 50 : 30,
    paddingBottom: 20,
    paddingHorizontal: 20,
  },
  itemDetailHeaderContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  backButtonTop: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 8,
  },
  backButtonText: {
    color: 'white',
    fontSize: 15,
    fontWeight: '600',
    marginLeft: 6,
  },
  itemDetailTitle: {
    color: 'white',
    fontSize: 18,
    fontWeight: '800',
  },
  itemDetailContent: {
    padding: 20,
  },
  itemDetailCard: {
    backgroundColor: BRAND.surface,
    borderRadius: 16,
    padding: 20,
    elevation: 4,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
  },
  itemDetailName: {
    fontSize: 22,
    fontWeight: '800',
    color: BRAND.text,
    marginBottom: 10,
    lineHeight: 28,
  },
  itemDetailDescription: {
    fontSize: 15,
    color: BRAND.textSecondary,
    marginBottom: 20,
    lineHeight: 22,
  },
  itemDetailStatus: {
    flexDirection: 'row',
    marginBottom: 20,
  },
  detailBadge: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 12,
    marginRight: 10,
    borderWidth: 1,
    borderColor: 'transparent',
  },
  detailBadgeText: {
    fontSize: 12,
    fontWeight: '800',
    color: 'white',
    textTransform: 'uppercase',
  },
  detailSection: {
    marginBottom: 24,
  },
  detailSectionTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: BRAND.text,
    marginBottom: 12,
    borderBottomWidth: 1,
    borderBottomColor: BRAND.border,
    paddingBottom: 8,
  },
  detailGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginHorizontal: -8,
  },
  detailItem: {
    width: '50%',
    paddingHorizontal: 8,
    marginBottom: 12,
  },
  detailLabel: {
    fontSize: 12,
    color: BRAND.textSecondary,
    marginBottom: 4,
    fontWeight: '600',
  },
  detailValue: {
    fontSize: 14,
    color: BRAND.text,
    fontWeight: '600',
  },
  financialDetails: {
    backgroundColor: BRAND.background,
    borderRadius: 12,
    padding: 16,
  },
  financialItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: BRAND.border,
  },
  projectDetails: {
    backgroundColor: BRAND.background,
    borderRadius: 12,
    padding: 16,
  },
  projectItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: BRAND.border,
  },
  projectText: {
    fontSize: 14,
    color: BRAND.text,
    marginLeft: 10,
    fontWeight: '600',
  },
  tagsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  detailTag: {
    backgroundColor: BRAND.background,
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 8,
    marginRight: 8,
    marginBottom: 8,
    borderWidth: 1,
    borderColor: BRAND.border,
  },
  detailTagText: {
    fontSize: 12,
    color: BRAND.text,
    fontWeight: '600',
  },
  detailActions: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 20,
  },
  detailActionButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 14,
    borderRadius: 10,
    marginHorizontal: 6,
  },
  detailActionText: {
    fontSize: 14,
    fontWeight: '600',
    marginLeft: 6,
  },

  // Action Buttons
  addEventButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: BRAND.primary,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
  },
  addEventText: {
    color: 'white',
    fontSize: 13,
    fontWeight: '600',
    marginLeft: 4,
  },
  addButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: BRAND.primary,
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: 8,
  },
  addButtonText: {
    color: 'white',
    fontSize: 13,
    fontWeight: '600',
    marginLeft: 4,
  },

  // FABs - Enhanced
  fab: { 
    position: 'absolute', 
    bottom: 30, 
    right: 20, 
    width: 60, 
    height: 60, 
    borderRadius: 30,
    justifyContent: 'center',
    alignItems: 'center',
    elevation: 8,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 3 },
    shadowOpacity: 0.3,
    shadowRadius: 6,
  },
  fabAI: { 
    position: 'absolute', 
    bottom: 30, 
    left: 20, 
  },
  aiFab: {
    width: 52,
    height: 52,
    borderRadius: 26,
    justifyContent: 'center',
    alignItems: 'center',
    elevation: 8,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 3 },
    shadowOpacity: 0.3,
    shadowRadius: 6,
  },
});

export default ModuleDetailScreen;